// ----------------------------------------------------------------------------
// timeline — canvas-based track view for DAWSEUR
//
// Features:
//   - Multi-select (click, shift+click, ctrl+click, drag-select box)
//   - Tools: select, pencil (paint events), cut/copy/paste/delete
//   - Volume handles on top of each event (drag to adjust per-event volume)
//   - Custom context menu (right-click): cut, copy, paste, duplicate, delete, move to track
//   - Horizontal scroll (shift+wheel or drag ruler)
//   - Mouse wheel zoom (cursor-centered)
//   - Intelligent snap + granular grid
//   - Event move, resize (edge drag), delete
// ----------------------------------------------------------------------------

const { sampleCurve } = require('./akima.js');

function createTimeline(canvas, opts = {}) {
   const ctx = canvas.getContext('2d');
   const state = {
      bpm: 120,
      beatsPerMeasure: 4,
      pxPerBeat: 30,
      trackHeight: 80,
      rulerHeight: 24,
      timeRulerHeight: 16,
      scrollTop: 0,
      scrollLeft: 0,         // horizontal scroll offset in beats
      totalBeats: 64,        // total content length in beats (grows as events are added)
      scrollbarEl: null,
      scrollbarHandleEl: null,
      scrollbarDragging: false,
      scrollbarDragStartX: 0,
      scrollbarDragStartScroll: 0,
      tracks: [],
      playheadBeat: 0,
      isPlaying: false,
      loopEnabled: false,
      loopStart: 0,           // beat
      loopEnd: 4,             // beat
      selectedEvents: [],     // array of { trackId, eventIdx }
      dragMode: null,         // 'move', 'resize-left', 'resize-right', 'volume', 'select-box', 'ruler-drag'
      dragData: null,
      snapEnabled: true,
      tool: 'select',         // 'select', 'pencil'
      clipboard: [],          // [{ startBeat, lengthBeats, buffer, acidMeta, volume }]
      eventClipboard: null,   // true when event clipboard has data (for context menu paste visibility)
      contextMenuEl: null,
      contextBeat: null,      // beat position where context menu was opened (for paste)
      contextTrackIdx: null,  // track index where context menu was opened (for new automation)
      contextAutoClip: null,  // auto clip that was right-clicked (for auto menu actions)
      contextAutoParam: null, // param name when right-clicking on auto lane empty area
      contextAutoParentId: null, // parent track id when right-clicking on auto lane empty area
      selectedAutoClip: null, // currently selected auto clip (for keyboard shortcuts)
      edgeScrollTimer: null,  // interval for edge auto-scroll during drag
      edgeScrollDir: 0,       // -1=left, 0=none, 1=right
      edgeScrollStrength: 0,  // 0-1 scroll speed multiplier
      onEventSelected: opts.onEventSelected || (() => {}),
      onEventCreated: opts.onEventCreated || (() => {}),
      onEventDeleted: opts.onEventDeleted || (() => {}),
      onPlayheadMoved: opts.onPlayheadMoved || (() => {}),
      onEventsChanged: opts.onEventsChanged || (() => {}),
      onLoopChanged: opts.onLoopChanged || (() => {}),
      onAutomationClick: opts.onAutomationClick || (() => {}),
      onAutoLaneClick: opts.onAutoLaneClick || (() => {}),
      onAutomationDragged: opts.onAutomationDragged || (() => {}),
      autoClipboard: null,
      automations: [],
      autoTracks: [], // [{ id, parentId, paramName, points, startBeat, lengthBeats, min, max }]
      // ---- FX chain snapshot/restore callbacks (set by dawseur.js) ----
      snapshotFxChains: opts.snapshotFxChains || null,
      restoreFxChains: opts.restoreFxChains || null,
      // ---- Undo/Redo history ----
      undoStack: [],
      redoStack: [],
      maxHistory: 50,
   };

   // ---- History snapshot / restore ----
   function snapshotState() {
      // Deep-clone tracks (events are plain objects except buffer which is shared by ref)
      const tracks = state.tracks.map(t => ({
         id: t.id,
         name: t.name,
         color: t.color,
         muted: t.muted,
         solo: t.solo,
         events: t.events.map(ev => ({
            startBeat: ev.startBeat,
            lengthBeats: ev.lengthBeats,
            volume: ev.volume,
            stretched: ev.stretched || false,
            buffer: ev.buffer,           // shared by reference (AudioBuffer)
            acidMeta: ev.acidMeta,
            fadeInBeats: ev.fadeInBeats || 0,
            fadeOutBeats: ev.fadeOutBeats || 0,
            fadeInCp1X: ev.fadeInCp1X, fadeInCp1Y: ev.fadeInCp1Y,
            fadeInCp2X: ev.fadeInCp2X, fadeInCp2Y: ev.fadeInCp2Y,
            fadeOutCp1X: ev.fadeOutCp1X, fadeOutCp1Y: ev.fadeOutCp1Y,
            fadeOutCp2X: ev.fadeOutCp2X, fadeOutCp2Y: ev.fadeOutCp2Y,
            _reversedBuffer: ev._reversedBuffer,
         })),
      }));
      const autoTracks = state.autoTracks.map(a => ({ ...a, points: a.points ? a.points.map(p => ({ ...p })) : [] }));
      // Snapshot FX chains via callback (serializes to plain data)
      const fxChains = state.snapshotFxChains ? state.snapshotFxChains() : null;
      return { tracks, autoTracks, fxChains };
   }

   function restoreState(snap) {
      state.tracks = snap.tracks.map(t => ({
         id: t.id,
         name: t.name,
         color: t.color,
         muted: t.muted,
         solo: t.solo,
         events: t.events.map(ev => ({ ...ev })),
      }));
      state.autoTracks = snap.autoTracks.map(a => ({ ...a, points: a.points ? a.points.map(p => ({ ...p })) : [] }));
      // Restore FX chains via callback (recreates engine FX from plain data)
      if (state.restoreFxChains && snap.fxChains !== undefined) {
         state.restoreFxChains(snap.fxChains);
      }
      // Clear selection (indices may be invalid after restore)
      state.selectedEvents = [];
      state.onEventSelected(null);
   }

   function pushHistory() {
      state.undoStack.push(snapshotState());
      if (state.undoStack.length > state.maxHistory) state.undoStack.shift();
      state.redoStack = []; // clear redo on new action
   }

   // External API for pushing history (e.g. from auto editor before curve edits)
   function pushHistoryForExternal() {
      pushHistory();
   }

   function undo() {
      if (state.undoStack.length === 0) return;
      state.redoStack.push(snapshotState());
      const snap = state.undoStack.pop();
      restoreState(snap);
      state.onEventsChanged();
      render();
   }

   function redo() {
      if (state.redoStack.length === 0) return;
      state.undoStack.push(snapshotState());
      const snap = state.redoStack.pop();
      restoreState(snap);
      state.onEventsChanged();
      render();
   }

   // ---- Theme colors ----
   function getThemeColors() {
      const style = getComputedStyle(document.body);
      const get = (name, fallback) => style.getPropertyValue(name).trim() || fallback;
      return {
         bg: get('--c0', '#000000'),
         bg2: get('--c1', '#0f0f0f'),
         grid: get('--c2', '#1e1e1e'),
         gridMajor: get('--c3', '#2d2d2d'),
         text: get('--ce', '#d2d2d2'),
         textBright: get('--cf', '#e1e1e1'),
         accent: get('--cb', '#a5a5a5'),
         muted: get('--c6', '#5a5a5a'),
      };
   }

   // ---- Sizing ----
   function resize() {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return; // not laid out yet
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      // Setting canvas.width resets the transform, so re-apply scale
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      render();
   }

   function getCanvasSize() {
      const rect = canvas.getBoundingClientRect();
      return { w: rect.width, h: rect.height };
   }

   // ---- Coordinate conversion (accounts for scrollLeft) ----
   function beatToX(beat) {
      return (beat - state.scrollLeft) * state.pxPerBeat;
   }

   function xToBeat(x) {
      return x / state.pxPerBeat + state.scrollLeft;
   }

   // Event width in pixels (does NOT depend on scrollLeft)
   function eventWidth(lengthBeats) {
      return lengthBeats * state.pxPerBeat;
   }

   function snapBeat(beat) {
      if (!state.snapEnabled) return beat;
      const sv = getSnapValue();
      return Math.round(beat / sv) * sv;
   }

   function getSnapValue() {
      const ppb = state.pxPerBeat;
      if (ppb < 5)   return state.beatsPerMeasure * 4;
      if (ppb < 10)  return state.beatsPerMeasure * 2;
      if (ppb < 15)  return state.beatsPerMeasure;
      if (ppb < 30)  return 1;
      if (ppb < 60)  return 0.5;
      if (ppb < 120) return 0.25;
      if (ppb < 240) return 0.125;
      return 0.0625;
   }

   function getGridDivisions() {
      const ppb = state.pxPerBeat;
      if (ppb < 5)   return 0;      // zoomed out very far: only measure lines
      if (ppb < 10)  return 0;      // only measure lines
      if (ppb < 15)  return 1;      // beat lines only
      if (ppb < 30)  return 1;
      if (ppb < 60)  return 2;
      if (ppb < 120) return 4;
      if (ppb < 240) return 8;
      return 16;
   }

   function trackIndexToY(idx) {
      return state.rulerHeight + idx * state.trackHeight - state.scrollTop;
   }

   function yToTrackIndex(y) {
      return Math.floor((y - state.rulerHeight + state.scrollTop) / state.trackHeight);
   }

   // ---- Selection helpers ----
   function isEventSelected(trackId, eventIdx) {
      return state.selectedEvents.some(s => s.trackId === trackId && s.eventIdx === eventIdx);
   }

   function selectEvent(trackId, eventIdx, additive = false, toggle = false) {
      if (toggle && isEventSelected(trackId, eventIdx)) {
         state.selectedEvents = state.selectedEvents.filter(s => !(s.trackId === trackId && s.eventIdx === eventIdx));
      } else if (additive) {
         if (!isEventSelected(trackId, eventIdx)) {
            state.selectedEvents.push({ trackId, eventIdx });
         }
      } else {
         state.selectedEvents = [{ trackId, eventIdx }];
      }
   }

   function clearSelection() {
      state.selectedEvents = [];
   }

   function selectAll() {
      state.selectedEvents = [];
      for (let i = 0; i < state.tracks.length; i++) {
         for (let j = 0; j < state.tracks[i].events.length; j++) {
            state.selectedEvents.push({ trackId: state.tracks[i].id, eventIdx: j });
         }
      }
   }

   // ---- Get selected event objects ----
   function getSelectedEvents() {
      return state.selectedEvents.map(s => {
         const track = state.tracks.find(t => t.id === s.trackId);
         if (!track) return null;
         return { trackId: s.trackId, eventIdx: s.eventIdx, event: track.events[s.eventIdx] };
      }).filter(Boolean);
   }

   // ---- Clipboard: copy/cut/paste/duplicate ----
   // Clone all relevant properties of an event for clipboard/duplicate.
   // Handles both audio clips (buffer) and MIDI clips (type/sequence/pattern).
   function cloneEventProps(ev) {
      const clone = {
         lengthBeats: ev.lengthBeats,
         volume: ev.volume || 1.0,
         stretched: ev.stretched || false,
         fadeInBeats: ev.fadeInBeats || 0,
         fadeOutBeats: ev.fadeOutBeats || 0,
         fadeInCp1X: ev.fadeInCp1X, fadeInCp1Y: ev.fadeInCp1Y,
         fadeInCp2X: ev.fadeInCp2X, fadeInCp2Y: ev.fadeInCp2Y,
         fadeOutCp1X: ev.fadeOutCp1X, fadeOutCp1Y: ev.fadeOutCp1Y,
         fadeOutCp2X: ev.fadeOutCp2X, fadeOutCp2Y: ev.fadeOutCp2Y,
      };
      if (ev.type) {
         // MIDI clip (stepseq / slicer / pianoroll)
         clone.type = ev.type;
         if (ev.sequence) clone.sequence = ev.sequence; // shared by ref — edits propagate
         if (ev.pattern)  clone.pattern  = ev.pattern;  // shared by ref — edits propagate
      } else {
         // Audio clip
         clone.buffer = ev.buffer;           // shared by ref (AudioBuffer)
         clone.acidMeta = ev.acidMeta;
         clone._reversedBuffer = ev._reversedBuffer;
      }
      return clone;
   }

   function copySelected() {
      const selected = getSelectedEvents();
      if (selected.length === 0) return;
      // Find leftmost beat for relative positioning
      const minBeat = Math.min(...selected.map(s => s.event.startBeat));
      state.clipboard = selected.map(s => ({
         trackId: s.trackId,   // preserve source track for multi-track paste
         offsetBeat: s.event.startBeat - minBeat,
         ...cloneEventProps(s.event),
      }));
      state.eventClipboard = true;
   }

   function cutSelected() {
      copySelected();
      deleteSelected();
   }

   function pasteAt(beat) {
      if (state.clipboard.length === 0) return;
      pushHistory();
      const targetBeat = snapBeat(beat);
      // Default target track: first track, or the track of the first selected event
      let defaultTrackId = state.tracks[0]?.id;
      const sel = getSelectedEvents();
      if (sel.length > 0) defaultTrackId = sel[0].trackId;

      const newSelection = [];
      for (const clip of state.clipboard) {
         // Respect the clip's original track; fall back to default if it no longer exists
         let trackId = clip.trackId;
         let track = state.tracks.find(t => t.id === trackId);
         if (!track) { trackId = defaultTrackId; track = state.tracks.find(t => t.id === trackId); }
         if (!track) continue;
         const newEvent = {
            startBeat: targetBeat + clip.offsetBeat,
            ...cloneEventProps(clip),
         };
         track.events.push(newEvent);
         newSelection.push({ trackId: trackId, eventIdx: track.events.length - 1 });
      }
      state.selectedEvents = newSelection;
      state.onEventsChanged();
      render();
   }

   function duplicateSelected() {
      const selected = getSelectedEvents();
      if (selected.length === 0) return;
      pushHistory();
      const minBeat = Math.min(...selected.map(s => s.event.startBeat));
      const maxEnd = Math.max(...selected.map(s => s.event.startBeat + s.event.lengthBeats));
      const offset = snapBeat(maxEnd - minBeat);

      const newSelection = [];
      for (const s of selected) {
         const track = state.tracks.find(t => t.id === s.trackId);
         if (!track) continue;
         const newEvent = {
            startBeat: s.event.startBeat + offset,
            ...cloneEventProps(s.event),
         };
         track.events.push(newEvent);
         newSelection.push({ trackId: s.trackId, eventIdx: track.events.length - 1 });
      }
      state.selectedEvents = newSelection;
      state.onEventsChanged();
      render();
   }

   function splitSelected(splitBeat) {
      const beat = splitBeat !== undefined ? snapBeat(splitBeat) : snapBeat(state.playheadBeat);
      const selected = getSelectedEvents();
      if (selected.length === 0) return;
      pushHistory();
      const newSelection = [];
      for (const s of selected) {
         const track = state.tracks.find(t => t.id === s.trackId);
         if (!track) continue;
         const event = s.event;
         const evStart = event.startBeat;
         const evEnd = event.startBeat + event.lengthBeats;
         // Only split if the playhead is strictly inside the clip
         if (beat <= evStart || beat >= evEnd) {
            newSelection.push(s);
            continue;
         }
         const firstLen = beat - evStart;
         const secondLen = evEnd - beat;
         // Modify the original event to be the first half
         event.lengthBeats = firstLen;
         // Create the second half
         const secondEvent = {
            startBeat: beat,
            lengthBeats: secondLen,
            buffer: event.buffer,
            acidMeta: event.acidMeta,
            volume: event.volume || 1.0,
            stretched: event.stretched || false,
            // No fades on the split halves — user can re-add if needed
            fadeInBeats: 0,
            fadeOutBeats: event.fadeOutBeats || 0,  // keep fade-out on the second half
         };
         // The first half keeps its fade-in, but fade-out is removed
         event.fadeOutBeats = 0;
         track.events.push(secondEvent);
         newSelection.push({ trackId: s.trackId, eventIdx: track.events.length - 1 });
      }
      state.selectedEvents = newSelection;
      state.onEventsChanged();
      render();
   }

   function reverseSelected() {
      const selected = getSelectedEvents();
      if (selected.length === 0) return;
      pushHistory();
      for (const s of selected) {
         const event = s.event;
         if (!event.buffer) continue;
         // Create a reversed copy of the buffer if not already reversed
         if (!event._reversedBuffer) {
            const ctx = state.audioCtx || (window.audioCtx);
            if (!ctx) continue;
            const orig = event.buffer;
            const reversed = ctx.createBuffer(orig.numberOfChannels, orig.length, orig.sampleRate);
            for (let ch = 0; ch < orig.numberOfChannels; ch++) {
               const srcData = orig.getChannelData(ch);
               const dstData = reversed.getChannelData(ch);
               const len = srcData.length;
               for (let i = 0; i < len; i++) {
                  dstData[i] = srcData[len - 1 - i];
               }
            }
            event._reversedBuffer = reversed;
         }
         // Swap: use the reversed buffer, keep the original for toggling back
         const tmp = event.buffer;
         event.buffer = event._reversedBuffer;
         event._reversedBuffer = tmp;
         // Clear any cached stretched buffer since the source changed
         event._stretchedCache = null;
         event._stretchedCacheKey = null;
      }
      state.onEventsChanged();
      render();
   }

   function deleteSelected() {
      if (state.selectedEvents.length === 0) return;
      pushHistory();
      // Sort by eventIdx descending so removals don't shift indices
      const sorted = [...state.selectedEvents].sort((a, b) => {
         if (a.trackId !== b.trackId) return 0;
         return b.eventIdx - a.eventIdx;
      });
      for (const s of sorted) {
         const track = state.tracks.find(t => t.id === s.trackId);
         if (!track) continue;
         const event = track.events[s.eventIdx];
         track.events.splice(s.eventIdx, 1);
         state.onEventDeleted(s.trackId, event);
      }
      state.selectedEvents = [];
      state.onEventsChanged();
      render();
   }

   // ---- Move selected events to a different track ----
   function moveToTrack(targetTrackId) {
      const selected = getSelectedEvents();
      if (selected.length === 0) return;
      const targetTrack = state.tracks.find(t => t.id === targetTrackId);
      if (!targetTrack) return;
      pushHistory();

      // Sort descending for removal
      const sorted = [...state.selectedEvents].sort((a, b) => {
         if (a.trackId !== b.trackId) return 0;
         return b.eventIdx - a.eventIdx;
      });
      const movedEvents = [];
      for (const s of sorted) {
         const track = state.tracks.find(t => t.id === s.trackId);
         if (!track) continue;
         const event = track.events.splice(s.eventIdx, 1)[0];
         movedEvents.push(event);
      }
      // Add to target track
      for (const ev of movedEvents) {
         targetTrack.events.push(ev);
      }
      // Update selection to new positions
      state.selectedEvents = movedEvents.map(() => ({
         trackId: targetTrackId,
         eventIdx: targetTrack.events.indexOf(movedEvents[0]), // approximate
      }));
      // Fix selection indices
      state.selectedEvents = [];
      for (let j = 0; j < targetTrack.events.length; j++) {
         if (movedEvents.includes(targetTrack.events[j])) {
            state.selectedEvents.push({ trackId: targetTrackId, eventIdx: j });
         }
      }
      state.onEventsChanged();
      render();
   }

   // ---- Rendering ----
   function render() {
      const { w, h } = getCanvasSize();
      const colors = getThemeColors();

      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, w, h);

      // ---- 1. Track lane backgrounds ----
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, state.rulerHeight, w, h - state.rulerHeight - state.timeRulerHeight);
      ctx.clip();

      for (let i = 0; i < state.tracks.length; i++) {
         const track = state.tracks[i];
         const y = trackIndexToY(i);
         if (y + state.trackHeight < state.rulerHeight) continue;
         if (y > h) continue;

         ctx.fillStyle = i % 2 === 0 ? colors.bg : colors.bg2;
         ctx.fillRect(0, y, w, state.trackHeight);

         ctx.strokeStyle = colors.grid;
         ctx.lineWidth = 0.5;
         ctx.beginPath();
         ctx.moveTo(0, y + state.trackHeight);
         ctx.lineTo(w, y + state.trackHeight);
         ctx.stroke();

         ctx.fillStyle = colors.muted;
         ctx.font = '9px monospace';
         ctx.fillText(track.name || `Trk ${i+1}`, 4, y + 10);
      }

      // ---- 2. Grid lines ----
      const divisions = getGridDivisions();
      if (divisions === 0) {
         // Zoomed out very far: only draw measure lines
         const measure = state.beatsPerMeasure;
         const startBeat = Math.floor(state.scrollLeft / measure) * measure;
         const endBeat = state.scrollLeft + w / state.pxPerBeat + measure;
         const total = Math.ceil((endBeat - startBeat) / measure) + 2;
         for (let i = 0; i < total; i++) {
            const beat = startBeat + i * measure;
            const x = beatToX(beat);
            if (x < -2 || x > w + 2) continue;
            ctx.strokeStyle = colors.gridMajor;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x, state.rulerHeight);
            ctx.lineTo(x, h - state.timeRulerHeight);
            ctx.stroke();
         }
      } else {
         const beatStep = 1 / divisions;
         const startBeat = Math.floor(state.scrollLeft / beatStep) * beatStep;
         const endBeat = state.scrollLeft + w / state.pxPerBeat + beatStep;
         const totalSubBeats = Math.ceil((endBeat - startBeat) / beatStep) + 2;

         for (let i = 0; i < totalSubBeats; i++) {
            const subBeat = startBeat + i * beatStep;
            const x = beatToX(subBeat);
            if (x < -2 || x > w + 2) continue;
            const isMeasure = Math.abs(subBeat % state.beatsPerMeasure) < 0.001;
            const isBeat = Math.abs(subBeat % 1) < 0.001;

            if (isMeasure) {
               ctx.strokeStyle = colors.gridMajor;
               ctx.lineWidth = 1;
            } else if (isBeat) {
               ctx.strokeStyle = colors.grid;
               ctx.lineWidth = 0.7;
            } else {
               ctx.strokeStyle = colors.grid;
               ctx.lineWidth = 0.4;
               ctx.globalAlpha = 0.6;
            }
            ctx.beginPath();
            ctx.moveTo(x, state.rulerHeight);
            ctx.lineTo(x, h - state.timeRulerHeight);
            ctx.stroke();
            ctx.globalAlpha = 1;
         }
      }

      // ---- 3. Selection box (if dragging) ----
      if (state.dragMode === 'select-box' && state.dragData) {
         const { startX, startY, currentX, currentY } = state.dragData;
         const sx = Math.min(startX, currentX);
         const sy = Math.min(startY, currentY);
         const sw = Math.abs(currentX - startX);
         const sh = Math.abs(currentY - startY);
         ctx.strokeStyle = colors.accent;
         ctx.lineWidth = 1;
         ctx.setLineDash([3, 3]);
         ctx.strokeRect(sx, sy, sw, sh);
         ctx.setLineDash([]);
         ctx.fillStyle = colors.accent;
         ctx.globalAlpha = 0.1;
         ctx.fillRect(sx, sy, sw, sh);
         ctx.globalAlpha = 1;
      }

      // ---- 4. Events ----
      for (let i = 0; i < state.tracks.length; i++) {
         const track = state.tracks[i];
         const y = trackIndexToY(i);
         if (y + state.trackHeight < state.rulerHeight) continue;
         if (y > h) continue;

         // Pre-compute overlap pixel intervals per event.
         // For each event, find the x-ranges where it overlaps with another event.
         // These regions will be drawn at reduced alpha so both waveforms show through.
         const overlapIntervalsByEvent = new Map();
         for (let a = 0; a < track.events.length; a++) {
            for (let b = a + 1; b < track.events.length; b++) {
               const aStart = track.events[a].startBeat;
               const aEnd = aStart + track.events[a].lengthBeats;
               const bStart = track.events[b].startBeat;
               const bEnd = bStart + track.events[b].lengthBeats;
               const lo = Math.max(aStart, bStart);
               const hi = Math.min(aEnd, bEnd);
               if (lo < hi) {
                  const loPx = beatToX(lo);
                  const hiPx = beatToX(hi);
                  for (const idx of [a, b]) {
                     if (!overlapIntervalsByEvent.has(idx)) overlapIntervalsByEvent.set(idx, []);
                     overlapIntervalsByEvent.get(idx).push([loPx, hiPx]);
                  }
               }
            }
         }
         // Merge intervals per event
         for (const [idx, intervals] of overlapIntervalsByEvent) {
            intervals.sort((a, b) => a[0] - b[0]);
            const merged = [intervals[0]];
            for (let k = 1; k < intervals.length; k++) {
               const last = merged[merged.length - 1];
               if (intervals[k][0] <= last[1]) last[1] = Math.max(last[1], intervals[k][1]);
               else merged.push(intervals[k]);
            }
            overlapIntervalsByEvent.set(idx, merged);
         }

         for (let j = 0; j < track.events.length; j++) {
            const event = track.events[j];
            const ex = beatToX(event.startBeat);
            const ew = eventWidth(event.lengthBeats);
            const ey = y + 14;
            const eh = state.trackHeight - 18;
            const selected = isEventSelected(track.id, j);
            const vol = event.volume !== undefined ? event.volume : 1.0;
            const overlapIntervals = overlapIntervalsByEvent.get(j) || [];

            // Helper: set clip to non-overlap regions of this event (ex..ex+ew minus overlapIntervals)
            function clipToNonOverlap() {
               ctx.save();
               ctx.beginPath();
               let cursor = ex;
               const end = ex + ew;
               for (const [lo, hi] of overlapIntervals) {
                  if (lo > cursor) ctx.rect(cursor, ey, lo - cursor, eh);
                  cursor = Math.max(cursor, hi);
               }
               if (cursor < end) ctx.rect(cursor, ey, end - cursor, eh);
               ctx.clip();
            }
            function clipToOverlap() {
               ctx.save();
               ctx.beginPath();
               for (const [lo, hi] of overlapIntervals) {
                  ctx.rect(lo, ey, hi - lo, eh);
               }
               ctx.clip();
            }
            function unclip() { ctx.restore(); }

            // Helper: evaluate 2D cubic bezier gain at evenly-spaced x position
            // P0=(0,y0), P1=(cp1x,cp1y), P2=(cp2x,cp2y), P3=(1,y3)
            // Binary-search for t where x(t)=xTarget, then return y(t)
            function splineGainAtX(xTarget, cp1x, cp1y, cp2x, cp2y, y0, y3) {
               let tLo = 0, tHi = 1;
               for (let iter = 0; iter < 20; iter++) {
                  const tMid = (tLo + tHi) * 0.5;
                  const xMid = 3*(1-tMid)*(1-tMid)*tMid*cp1x + 3*(1-tMid)*tMid*tMid*cp2x + tMid*tMid*tMid;
                  if (xMid < xTarget) tLo = tMid;
                  else tHi = tMid;
               }
               const t = (tLo + tHi) * 0.5;
               const mt = 1 - t;
               return mt*mt*mt*y0 + 3*mt*mt*t*cp1y + 3*mt*t*t*cp2y + t*t*t*y3;
            }

            // Fade in/out in pixels
            const fadeInPx = Math.min(ew, eventWidth(event.fadeInBeats || 0));
            const fadeOutPx = Math.min(ew, eventWidth(event.fadeOutBeats || 0));

            // Event block — background uses --c3 (gridMajor)
            // Non-overlap regions: full alpha; overlap regions: reduced alpha
            ctx.fillStyle = colors.gridMajor;
            // Non-overlap part
            clipToNonOverlap();
            ctx.globalAlpha = selected ? 1.0 : 0.8;
            ctx.fillRect(ex, ey, Math.max(ew, 2), eh);
            unclip();
            // Overlap part
            if (overlapIntervals.length > 0) {
               clipToOverlap();
               ctx.globalAlpha = selected ? 0.45 : 0.35;
               ctx.fillRect(ex, ey, Math.max(ew, 2), eh);
               unclip();
            }
            ctx.globalAlpha = 1;

            // Volume handle — small centered handle on top edge (only when zoomed in enough)
            // Skip for MIDI clips (volume is per-channel in the step sequencer)
            const isMidiClip = (event.type === 'stepseq' || event.type === 'slicer' || event.type === 'pianoroll');
            if (ew >= 20 && !isMidiClip) {
               const handleW = 16;
               const handleH = 5;
               const hx = ex + ew / 2 - handleW / 2;
               const hy = ey - handleH - 1;
               // Handle background
               ctx.fillStyle = colors.bg;
               ctx.fillRect(hx, hy, handleW, handleH);
               // Fill proportional to volume (0→empty, 1→full, 1.5→overflow)
               const fillW = Math.min(handleW, handleW * (vol / 1.0));
               ctx.fillStyle = colors.textBright;
               ctx.globalAlpha = 0.5 + vol * 0.5;
               ctx.fillRect(hx, hy, fillW, handleH);
               ctx.globalAlpha = 1;
               // Border
               ctx.strokeStyle = colors.textBright;
               ctx.lineWidth = 0.5;
               ctx.strokeRect(hx, hy, handleW, handleH);
            }

            // ---- MIDI clip rendering (step seq / slicer / piano roll) ----
            if (event.type === 'stepseq' || event.type === 'slicer' || event.type === 'pianoroll') {
               // Draw a pattern indicator instead of waveform
               ctx.fillStyle = colors.textBright;
               ctx.globalAlpha = 0.15;
               ctx.fillRect(ex, ey, ew, eh);
               ctx.globalAlpha = 1;

               // Compute sequence length in beats and loop count
               const stepsPerBeat = 4;
               const seqSteps = event.type === 'pianoroll'
                  ? (event.pattern ? event.pattern.beats * stepsPerBeat : 16)
                  : (event.sequence ? event.sequence.steps : 16);
               const seqLenBeats = seqSteps / stepsPerBeat;
               const seqLenPx = seqLenBeats * state.pxPerBeat;
               const numLoops = Math.max(1, Math.ceil(ew / seqLenPx));

               // Draw each loop iteration
               for (let loop = 0; loop < numLoops; loop++) {
                  const loopX = ex + loop * seqLenPx;
                  const loopW = Math.min(seqLenPx, ex + ew - loopX);
                  if (loopW <= 0) break;

                  const stepPx = seqLenPx / seqSteps;

                  // Draw step grid
                  ctx.strokeStyle = colors.text;
                  ctx.lineWidth = 0.3;
                  ctx.globalAlpha = 0.5;
                  const visibleSteps = Math.ceil(loopW / stepPx);
                  for (let s = 0; s <= visibleSteps; s++) {
                     if (s % 4 === 0 || s === seqSteps) {
                        const sx = loopX + s * stepPx;
                        if (sx > ex + ew) break;
                        ctx.beginPath();
                        ctx.moveTo(sx, ey);
                        ctx.lineTo(sx, ey + eh);
                        ctx.stroke();
                     }
                  }
                  ctx.globalAlpha = 1;

                  // Draw active steps
                  ctx.fillStyle = colors.textBright;
                  if (event.type === 'stepseq' && event.sequence) {
                     for (let ci = 0; ci < event.sequence.channels.length; ci++) {
                        const ch = event.sequence.channels[ci];
                        if (!ch.steps) continue;
                        const rowH = eh / Math.max(1, event.sequence.channels.length);
                        for (let s = 0; s < ch.steps.length && s < seqSteps; s++) {
                           if (ch.steps[s]) {
                              const sx = loopX + s * stepPx;
                              if (sx + stepPx > ex + ew) break;
                              ctx.globalAlpha = 0.6;
                              ctx.fillRect(sx + 1, ey + ci * rowH + 1, stepPx - 2, rowH - 2);
                           }
                        }
                     }
                  } else if (event.type === 'slicer' && event.sequence) {
                     for (let s = 0; s < event.sequence.pattern.length && s < seqSteps; s++) {
                        const slices = event.sequence.pattern[s];
                        if (slices && slices.length > 0) {
                           const sx = loopX + s * stepPx;
                           if (sx + stepPx > ex + ew) break;
                           ctx.globalAlpha = 0.6;
                           ctx.fillRect(sx + 1, ey + 1, stepPx - 2, eh - 2);
                        }
                     }
                  } else if (event.type === 'pianoroll' && event.pattern) {
                     for (const note of event.pattern.notes) {
                        const nx = loopX + note.startStep * stepPx;
                        const nw = note.lengthSteps * stepPx;
                        if (nx + nw > ex + ew) break;
                        // Vertical position based on MIDI note (higher notes at top)
                        const midiNote = note.midi !== undefined ? note.midi : (note.semitone !== undefined ? note.semitone + 60 : 60);
                        const totalSemis = 120;
                        const ny = ey + eh - (midiNote / totalSemis) * eh;
                        ctx.globalAlpha = 0.7;
                        ctx.fillRect(nx + 1, ny - 2, Math.max(2, nw - 2), 3);
                     }
                  }
                  ctx.globalAlpha = 1;

                  // Draw loop separator (dashed line between loops)
                  if (loop > 0) {
                     ctx.strokeStyle = colors.textBright;
                     ctx.globalAlpha = 0.3;
                     ctx.lineWidth = 1;
                     ctx.setLineDash([2, 2]);
                     ctx.beginPath();
                     ctx.moveTo(loopX, ey);
                     ctx.lineTo(loopX, ey + eh);
                     ctx.stroke();
                     ctx.setLineDash([]);
                     ctx.globalAlpha = 1;
                  }
               }

               // Draw type label
               ctx.fillStyle = colors.text;
               ctx.font = '8px bit, monospace';
               ctx.fillText(event.type, ex + 4, ey + eh - 4);
            }

            // Mini waveform — tiles/loops to fill the clip width when not stretched
            // Drawn in two passes: full alpha on non-overlap regions, reduced alpha on overlap regions
            if (event.buffer && ew > 8) {
               const bpm = state.bpm || 120;
               // If this event is being baked (was stretched, now resizing without Ctrl),
               // the "natural" length is the pre-resize length (_origLength) until the
               // buffer is baked on mouseup. Draw using that as the loop/trim boundary.
               const effectiveStretched = event.stretched || event._needsBake;
               const naturalBeats = event._needsBake
                  ? (event._origLength || event.buffer.duration * bpm / 60)
                  : (event.buffer.duration * bpm / 60);
               const naturalPx = naturalBeats * state.pxPerBeat;

               function drawWf(alphaScale) {
                  if (effectiveStretched) {
                     drawMiniWaveform(event.buffer, ex, ey, ew, eh, colors, vol, undefined, alphaScale);
                  } else if (naturalPx >= ew) {
                     drawMiniWaveform(event.buffer, ex, ey, ew, eh, colors, vol, naturalPx, alphaScale);
                  } else {
                     let tileX = 0;
                     while (tileX < ew) {
                        const tileW = Math.min(naturalPx, ew - tileX);
                        drawMiniWaveform(event.buffer, ex + tileX, ey, tileW, eh, colors, vol, naturalPx, alphaScale);
                        tileX += naturalPx;
                     }
                  }
               }

               // Non-overlap regions: full alpha
               clipToNonOverlap();
               drawWf(1);
               unclip();
               // Overlap regions: reduced alpha
               if (overlapIntervals.length > 0) {
                  clipToOverlap();
                  drawWf(0.5);
                  unclip();
               }
            }

            // ---- Fade In ---- (skip for MIDI clips)
            if (!isMidiClip) {
               const fiPx = Math.max(fadeInPx, 0);
               const cp1x = event.fadeInCp1X !== undefined ? event.fadeInCp1X : 1/3;
               const cp1y = event.fadeInCp1Y !== undefined ? event.fadeInCp1Y : 1/3;
               const cp2x = event.fadeInCp2X !== undefined ? event.fadeInCp2X : 2/3;
               const cp2y = event.fadeInCp2Y !== undefined ? event.fadeInCp2Y : 2/3;
               // Clamped values for curve calculation (stays in box)
               const cx1 = Math.max(0, Math.min(1, cp1x)), cy1 = Math.max(0, Math.min(1, cp1y));
               const cx2 = Math.max(0, Math.min(1, cp2x)), cy2 = Math.max(0, Math.min(1, cp2y));
               // Actual handle pixel positions (can be outside box)
               const p1x_px = ex + cp1x * fiPx, p1y_px = ey + eh - cp1y * eh;
               const p2x_px = ex + cp2x * fiPx, p2y_px = ey + eh - cp2y * eh;
               // Clamped handle pixel positions (on the border)
               const c1x_px = ex + cx1 * fiPx, c1y_px = ey + eh - cy1 * eh;
               const c2x_px = ex + cx2 * fiPx, c2y_px = ey + eh - cy2 * eh;

               if (fiPx > 2) {
                  const N = 40;
                  // Fill: area BELOW curve
                  ctx.fillStyle = colors.bg;
                  ctx.globalAlpha = 0.5;
                  ctx.beginPath();
                  ctx.moveTo(ex, ey + eh);
                  for (let i = 0; i <= N; i++) {
                     const xTarget = i / N;
                     const gain = splineGainAtX(xTarget, cx1, cy1, cx2, cy2, 0, 1);
                     const px = ex + xTarget * fiPx;
                     const py = ey + eh - gain * eh;
                     ctx.lineTo(px, py);
                  }
                  ctx.lineTo(ex + fiPx, ey + eh);
                  ctx.closePath();
                  ctx.fill();
                  ctx.globalAlpha = 1;

                  // Curve line
                  ctx.strokeStyle = colors.textBright;
                  ctx.lineWidth = 1.5;
                  ctx.beginPath();
                  for (let i = 0; i <= N; i++) {
                     const xTarget = i / N;
                     const gain = splineGainAtX(xTarget, cx1, cy1, cx2, cy2, 0, 1);
                     const px = ex + xTarget * fiPx;
                     const py = ey + eh - gain * eh;
                     if (i === 0) ctx.moveTo(px, py);
                     else ctx.lineTo(px, py);
                  }
                  ctx.stroke();

                  // Handles — actual position (outside box) with line to border
                  if (selected) {
                     for (const [[ax, ay], [cx, cy]] of [
                        [[p1x_px, p1y_px], [c1x_px, c1y_px]],
                        [[p2x_px, p2y_px], [c2x_px, c2y_px]],
                     ]) {
                        // Line from border to actual handle
                        if (ax !== cx || ay !== cy) {
                           ctx.strokeStyle = colors.textBright;
                           ctx.globalAlpha = 0.3;
                           ctx.lineWidth = 1;
                           ctx.setLineDash([2, 2]);
                           ctx.beginPath();
                           ctx.moveTo(cx, cy);
                           ctx.lineTo(ax, ay);
                           ctx.stroke();
                           ctx.setLineDash([]);
                           ctx.globalAlpha = 1;
                        }
                        // Handle circle at actual position
                        ctx.fillStyle = colors.bg;
                        ctx.beginPath();
                        ctx.arc(ax, ay, 4, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.strokeStyle = colors.textBright;
                        ctx.lineWidth = 1.5;
                        ctx.stroke();
                     }
                  }
               }

               // Fade-in length handle at top-left (only when zoomed in)
               if (ew >= 20) {
                  ctx.fillStyle = colors.textBright;
                  ctx.fillRect(ex + fiPx - 3, ey - 2, 6, 6);
                  ctx.strokeStyle = colors.bg;
                  ctx.lineWidth = 1;
                  ctx.strokeRect(ex + fiPx - 3, ey - 2, 6, 6);
               }
            }

            // ---- Fade Out ---- (skip for MIDI clips)
            if (!isMidiClip) // ---- Fade Out ----
            // Catmull-Rom spline: P0=(0,1)→H1→H2→P3=(1,0)
            // Handles free to move outside box; curve uses clamped positions
            {
               const foPx = Math.max(fadeOutPx, 0);
               const cp1x = event.fadeOutCp1X !== undefined ? event.fadeOutCp1X : 1/3;
               const cp1y = event.fadeOutCp1Y !== undefined ? event.fadeOutCp1Y : 2/3;
               const cp2x = event.fadeOutCp2X !== undefined ? event.fadeOutCp2X : 2/3;
               const cp2y = event.fadeOutCp2Y !== undefined ? event.fadeOutCp2Y : 1/3;
               // Clamped values for curve calculation
               const cx1 = Math.max(0, Math.min(1, cp1x)), cy1 = Math.max(0, Math.min(1, cp1y));
               const cx2 = Math.max(0, Math.min(1, cp2x)), cy2 = Math.max(0, Math.min(1, cp2y));
               // Actual handle pixel positions (can be outside box)
               const p1x_px = ex + ew - foPx + cp1x * foPx, p1y_px = ey + eh - cp1y * eh;
               const p2x_px = ex + ew - foPx + cp2x * foPx, p2y_px = ey + eh - cp2y * eh;
               // Clamped handle pixel positions (on the border)
               const c1x_px = ex + ew - foPx + cx1 * foPx, c1y_px = ey + eh - cy1 * eh;
               const c2x_px = ex + ew - foPx + cx2 * foPx, c2y_px = ey + eh - cy2 * eh;

               if (foPx > 2) {
                  const N = 40;
                  // Fill: area BELOW curve
                  ctx.fillStyle = colors.bg;
                  ctx.globalAlpha = 0.5;
                  ctx.beginPath();
                  ctx.moveTo(ex + ew - foPx, ey + eh);
                  for (let i = 0; i <= N; i++) {
                     const xTarget = i / N;
                     const gain = splineGainAtX(xTarget, cx1, cy1, cx2, cy2, 1, 0);
                     const px = ex + ew - foPx + xTarget * foPx;
                     const py = ey + eh - gain * eh;
                     ctx.lineTo(px, py);
                  }
                  ctx.lineTo(ex + ew, ey + eh);
                  ctx.closePath();
                  ctx.fill();
                  ctx.globalAlpha = 1;

                  // Curve line
                  ctx.strokeStyle = colors.textBright;
                  ctx.lineWidth = 1.5;
                  ctx.beginPath();
                  for (let i = 0; i <= N; i++) {
                     const xTarget = i / N;
                     const gain = splineGainAtX(xTarget, cx1, cy1, cx2, cy2, 1, 0);
                     const px = ex + ew - foPx + xTarget * foPx;
                     const py = ey + eh - gain * eh;
                     if (i === 0) ctx.moveTo(px, py);
                     else ctx.lineTo(px, py);
                  }
                  ctx.stroke();

                  // Handles — actual position with line to border
                  if (selected) {
                     for (const [[ax, ay], [cx, cy]] of [
                        [[p1x_px, p1y_px], [c1x_px, c1y_px]],
                        [[p2x_px, p2y_px], [c2x_px, c2y_px]],
                     ]) {
                        if (ax !== cx || ay !== cy) {
                           ctx.strokeStyle = colors.textBright;
                           ctx.globalAlpha = 0.3;
                           ctx.lineWidth = 1;
                           ctx.setLineDash([2, 2]);
                           ctx.beginPath();
                           ctx.moveTo(cx, cy);
                           ctx.lineTo(ax, ay);
                           ctx.stroke();
                           ctx.setLineDash([]);
                           ctx.globalAlpha = 1;
                        }
                        ctx.fillStyle = colors.bg;
                        ctx.beginPath();
                        ctx.arc(ax, ay, 4, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.strokeStyle = colors.textBright;
                        ctx.lineWidth = 1.5;
                        ctx.stroke();
                     }
                  }
               }

               // Fade-out length handle at top-right (only when zoomed in)
               if (ew >= 20) {
                  ctx.fillStyle = colors.textBright;
                  ctx.fillRect(ex + ew - foPx - 3, ey - 2, 6, 6);
                  ctx.strokeStyle = colors.bg;
                  ctx.lineWidth = 1;
                  ctx.strokeRect(ex + ew - foPx - 3, ey - 2, 6, 6);
               }
            }

            // Event border (drawn after fades so it's on top)
            ctx.strokeStyle = selected ? colors.textBright : colors.gridMajor;
            ctx.lineWidth = selected ? 2 : 1;
            ctx.strokeRect(ex, ey, Math.max(ew, 2), eh);
         }
      }

      // ---- 4b. Automation tracks (full 80px lanes, interleaved with audio tracks) ----
      // First pass: draw lane backgrounds once per unique trackIdx
      const drawnLanes = new Set();
      for (let i = 0; i < state.autoTracks.length; i++) {
         const auto = state.autoTracks[i];
         const trackIdx = auto.trackIdx !== undefined ? auto.trackIdx : (state.tracks.length + i);
         if (drawnLanes.has(trackIdx)) continue;
         drawnLanes.add(trackIdx);
         const y = trackIndexToY(trackIdx);
         if (y + state.trackHeight < state.rulerHeight) continue;
         if (y > h) continue;

         // Lane background
         ctx.fillStyle = colors.bg2;
         ctx.fillRect(0, y, w, state.trackHeight);
         ctx.strokeStyle = colors.gridMajor;
         ctx.lineWidth = 0.5;
         ctx.beginPath();
         ctx.moveTo(0, y);
         ctx.lineTo(w, y);
         ctx.stroke();
         ctx.beginPath();
         ctx.moveTo(0, y + state.trackHeight);
         ctx.lineTo(w, y + state.trackHeight);
         ctx.stroke();

         // Left border accent
         ctx.fillStyle = colors.gridMajor;
         ctx.fillRect(0, y, 3, state.trackHeight);

         // Param name label
         ctx.fillStyle = colors.text;
         ctx.font = '10px monospace';
         ctx.textAlign = 'left';
         ctx.fillText('\u2261 ' + auto.paramName, 4, y + 14);
      }

      // Second pass: draw all clips (background + curve + border) on top of lane backgrounds
      for (let i = 0; i < state.autoTracks.length; i++) {
         const auto = state.autoTracks[i];
         const trackIdx = auto.trackIdx !== undefined ? auto.trackIdx : (state.tracks.length + i);
         const y = trackIndexToY(trackIdx);
         if (y + state.trackHeight < state.rulerHeight) continue;
         if (y > h) continue;

         // Clip background (drawn BEFORE curve so it doesn't cover it)
         const ax = beatToX(auto.startBeat || 0);
         const aw = eventWidth(auto.lengthBeats || 4);
         const clipY = y + 16;
         const clipH = state.trackHeight - 18;
         const isAutoSelected = state.selectedAutoClip && state.selectedAutoClip.id === auto.id;
         ctx.fillStyle = isAutoSelected ? colors.gridMajor : colors.bg;
         ctx.fillRect(ax, clipY, aw, clipH);

         // Draw the automation curve using Akima spline sampling
         const min = auto.min !== undefined ? auto.min : 0;
         const max = auto.max !== undefined ? auto.max : 1;
         const pts = auto.points;
         if (pts && pts.length > 1 && aw > 0) {
            const curveY = y + 18;
            const curveH = state.trackHeight - 22;
            // Normalized y helper — clamped to [0,1], same for curve and knots
            const nyOf = (val) => Math.max(0, Math.min(1, (val - min) / (max - min)));
            const pyOf = (ny) => curveY + curveH - 2 - ny * (curveH - 4);

            // Sample the curve using akima interpolation
            const sortedPts = pts.slice().sort((a, b) => a.x - b.x);
            const numSamples = Math.max(40, Math.floor(aw / 3));
            const sampled = sampleCurve(sortedPts, numSamples);

            // Merge knot x-positions into the sampled points so the drawn curve
            // is guaranteed to pass exactly through each knot. Without this, the
            // evenly-spaced samples may skip over a knot x, and the polyline
            // between adjacent samples can miss the knot visually.
            const knotXs = new Set(sortedPts.map(p => p.x));
            const merged = [];
            let si = 0;
            for (const p of sortedPts) {
               // Add all sampled points before this knot
               while (si < sampled.length && sampled[si].x < p.x) {
                  merged.push(sampled[si]);
                  si++;
               }
               // Add the exact knot point (evaluated by interpolator at knot x)
               merged.push({ x: p.x, y: p.y });
               // Skip sampled point at same x (avoid duplicate)
               if (si < sampled.length && sampled[si].x === p.x) si++;
            }
            // Add remaining sampled points after last knot
            while (si < sampled.length) {
               merged.push(sampled[si]);
               si++;
            }

            ctx.strokeStyle = colors.accent;
            ctx.lineWidth = 2;
            ctx.beginPath();
            for (let s = 0; s < merged.length; s++) {
               const ny = nyOf(merged[s].y);
               const px = ax + merged[s].x * aw;
               const py = pyOf(ny);
               if (s === 0) ctx.moveTo(px, py);
               else ctx.lineTo(px, py);
            }
            ctx.stroke();
            // Draw knots — use same nyOf/pyOf so they align with the curve
            ctx.fillStyle = colors.text;
            for (const p of pts) {
               const ny = nyOf(p.y);
               const px = ax + p.x * aw;
               const py = pyOf(ny);
               ctx.beginPath();
               ctx.arc(px, py, 3, 0, Math.PI * 2);
               ctx.fill();
            }
         }

         // Clip border + resize handles (drawn AFTER curve)
         ctx.strokeStyle = isAutoSelected ? colors.textBright : colors.gridMajor;
         ctx.lineWidth = isAutoSelected ? 2 : 1;
         ctx.strokeRect(ax, clipY, aw, clipH);
         // Resize handles (left + right edges) — only when zoomed in enough
         if (aw >= 20) {
            ctx.fillStyle = isAutoSelected ? colors.textBright : colors.accent;
            ctx.fillRect(ax - 2, clipY, 4, clipH);
            ctx.fillRect(ax + aw - 2, clipY, 4, clipH);
         }
      }


      ctx.restore(); // end track area clip

      // ---- 5. Rulers (on top) ----
      ctx.fillStyle = colors.bg2;
      ctx.fillRect(0, 0, w, state.rulerHeight);
      ctx.fillStyle = colors.bg2;
      ctx.fillRect(0, h - state.timeRulerHeight, w, state.timeRulerHeight);

      // Beat ruler labels — adapt to zoom level to avoid clutter
      ctx.font = '10px monospace';
      ctx.textBaseline = 'middle';
      const measure = state.beatsPerMeasure;
      if (state.pxPerBeat < 10) {
         // Zoomed out far: only label measures, skip if too dense
         const labelStart = Math.floor(state.scrollLeft / measure) * measure;
         const labelEnd = state.scrollLeft + w / state.pxPerBeat + measure;
         for (let beat = labelStart; beat < labelEnd; beat += measure) {
            const x = beatToX(beat);
            if (x < -10 || x > w + 10) continue;
            const measureNum = beat / measure + 1;
            ctx.fillStyle = colors.textBright;
            ctx.fillText(`${measureNum}`, x + 3, state.rulerHeight / 2);
         }
      } else {
         const labelStartBeat = Math.floor(state.scrollLeft);
         const labelEndBeat = state.scrollLeft + w / state.pxPerBeat + 2;
         for (let beat = labelStartBeat; beat < labelEndBeat; beat++) {
            const x = beatToX(beat);
            if (x < -10 || x > w + 10) continue;
            const isMeasure = beat % measure === 0;
            const measureNum = beat / measure + 1;
            const beatInMeasure = beat % measure + 1;

            if (isMeasure) {
               ctx.fillStyle = colors.textBright;
               ctx.fillText(`${measureNum}`, x + 3, state.rulerHeight / 2);
            } else if (state.pxPerBeat > 20) {
               ctx.fillStyle = colors.text;
               ctx.fillText(`${beatInMeasure}`, x + 3, state.rulerHeight / 2);
            } else if (state.pxPerBeat > 10) {
               ctx.fillStyle = colors.text;
               ctx.fillText('.', x + 2, state.rulerHeight / 2);
            }
         }
      }

      // Sub-beat ticks
      if (divisions > 1 && state.pxPerBeat > 40) {
         ctx.fillStyle = colors.muted;
         const subStep = 1 / divisions;
         const subStart = Math.floor(state.scrollLeft / subStep) * subStep;
         const subEnd = state.scrollLeft + w / state.pxPerBeat + subStep;
         for (let sub = subStart; sub < subEnd; sub += subStep) {
            if (Math.abs(sub % 1) < 0.001) continue;
            const x = beatToX(sub);
            if (x < 0 || x > w) continue;
            ctx.fillText('·', x + 1, state.rulerHeight / 2);
         }
      }

      // Time ruler labels
      const secPerBeat = 60 / state.bpm;
      const pxPerSec = state.pxPerBeat / secPerBeat;
      const secStep = pxPerSec > 200 ? 0.5 : pxPerSec > 100 ? 1 : pxPerSec > 50 ? 2 : pxPerSec > 20 ? 5 : 10;
      ctx.fillStyle = colors.text;
      const secStart = Math.floor(state.scrollLeft * secPerBeat / secStep) * secStep;
      const secEnd = (state.scrollLeft + w / state.pxPerBeat) * secPerBeat + secStep;
      for (let s = secStart; s < secEnd; s += secStep) {
         const beat = s / secPerBeat;
         const x = beatToX(beat);
         if (x < -10 || x > w + 10) continue;
         const label = secStep < 1 ? `${s.toFixed(1)}s` : `${s}s`;
         ctx.fillText(label, x + 2, h - state.timeRulerHeight / 2);
      }

      // Loop region (highlighted area between loop points)
      if (state.loopEnabled) {
         const loopStartX = beatToX(state.loopStart);
         const loopEndX = beatToX(state.loopEnd);

         // Shaded region in track area
         ctx.fillStyle = colors.accent;
         ctx.globalAlpha = 0.08;
         ctx.fillRect(loopStartX, state.rulerHeight, loopEndX - loopStartX, h - state.rulerHeight - state.timeRulerHeight);
         ctx.globalAlpha = 1;

         // Loop start marker — vertical line through track area
         ctx.strokeStyle = colors.textBright;
         ctx.lineWidth = 1.5;
         ctx.setLineDash([4, 2]);
         ctx.beginPath();
         ctx.moveTo(loopStartX, state.rulerHeight);
         ctx.lineTo(loopStartX, h - state.timeRulerHeight);
         ctx.stroke();
         ctx.setLineDash([]);

         // Loop start handle — right-angle triangle pointing right (into loop)
         // 90° corner at top-left
         ctx.fillStyle = colors.textBright;
         ctx.beginPath();
         ctx.moveTo(loopStartX, 0);
         ctx.lineTo(loopStartX + 8, 0);
         ctx.lineTo(loopStartX, 8);
         ctx.closePath();
         ctx.fill();

         // Loop end marker — vertical line through track area
         ctx.strokeStyle = colors.textBright;
         ctx.lineWidth = 1.5;
         ctx.setLineDash([4, 2]);
         ctx.beginPath();
         ctx.moveTo(loopEndX, state.rulerHeight);
         ctx.lineTo(loopEndX, h - state.timeRulerHeight);
         ctx.stroke();
         ctx.setLineDash([]);

         // Loop end handle — right-angle triangle pointing left (into loop)
         // 90° corner at top-right
         ctx.fillStyle = colors.textBright;
         ctx.beginPath();
         ctx.moveTo(loopEndX, 0);
         ctx.lineTo(loopEndX - 8, 0);
         ctx.lineTo(loopEndX, 8);
         ctx.closePath();
         ctx.fill();
      }

      // Playhead
      const playheadX = beatToX(state.playheadBeat);
      if (playheadX >= 0 && playheadX <= w) {
         ctx.strokeStyle = colors.accent;
         ctx.lineWidth = 1.5;
         ctx.beginPath();
         ctx.moveTo(playheadX, state.rulerHeight);
         ctx.lineTo(playheadX, h - state.timeRulerHeight);
         ctx.stroke();

         ctx.fillStyle = colors.accent;
         ctx.beginPath();
         ctx.moveTo(playheadX - 5, 0);
         ctx.lineTo(playheadX + 5, 0);
         ctx.lineTo(playheadX, 6);
         ctx.closePath();
         ctx.fill();
      }

      // Update scrollbar after render
      updateScrollbarHandle();
   }

   function drawMiniWaveform(buffer, x, y, w, h, colors, vol, sourceWidth, alphaScale) {
      alphaScale = alphaScale !== undefined ? alphaScale : 1;
      const data = buffer.getChannelData(0);
      const samples = data.length;
      // sourceWidth: pixel width that the full buffer maps to (for tiling)
      // If not provided, map the full buffer to w (original behavior)
      const mapW = sourceWidth || w;
      const step = Math.max(1, Math.floor(samples / mapW));
      const midY = y + h / 2;
      const amplitude = (vol !== undefined ? vol : 1.0);
      const maxAmp = h / 2 - 1;  // clamp so waveform never overflows the event block

      // Find peak amplitude in the buffer for display normalization
      // (buffer may be scaled down for audio, but waveform should fill the clip visually)
      let peak = 0;
      for (let i = 0; i < samples; i += 64) { // sample every 64th for speed
         const v = Math.abs(data[i]);
         if (v > peak) peak = v;
      }
      const normFactor = peak > 0 ? 1 / peak : 1;

      // Clip to event block bounds so waveform never overflows
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();

      ctx.strokeStyle = colors.textBright;
      ctx.globalAlpha = 0.5 * alphaScale;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let px = 0; px < w; px++) {
         // Map pixel position to buffer sample
         const samplePos = Math.floor(px * samples / mapW);
         const start = samplePos;
         const end = Math.min(start + step, samples);
         let max = 0;
         for (let s = start; s < end; s++) {
            const v = Math.abs(data[s]);
            if (v > max) max = v;
         }
         // Normalize to peak so waveform fills the clip height regardless of buffer scaling
         const amp = Math.min(max * normFactor * maxAmp * amplitude, maxAmp);
         ctx.moveTo(x + px, midY - amp);
         ctx.lineTo(x + px, midY + amp);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.restore();
   }

   // ---- Hit testing ----
   function hitTest(x, y) {
      // Check loop handles first (they're on top of ruler, highest priority)
      if (state.loopEnabled && y < state.rulerHeight) {
         const loopStartX = beatToX(state.loopStart);
         const loopEndX = beatToX(state.loopEnd);
         if (Math.abs(x - loopStartX) < 8) return { mode: 'loop-start', beat: xToBeat(x) };
         if (Math.abs(x - loopEndX) < 8) return { mode: 'loop-end', beat: xToBeat(x) };
      }

      // Early pass: check curve handles for selected events across ALL tracks.
      // Handles can be dragged outside the clip box (above/below tracks), so we
      // must check them before the track index validation.
      const fadeHandleR = 7;
      for (let ti = 0; ti < state.tracks.length; ti++) {
         const track = state.tracks[ti];
         const ey_t = trackIndexToY(ti) + 14;
         const eh_t = state.trackHeight - 18;
         for (let j = track.events.length - 1; j >= 0; j--) {
            if (!isEventSelected(track.id, j)) continue;
            const event = track.events[j];
            if (event.type === 'stepseq' || event.type === 'slicer' || event.type === 'pianoroll') continue;
            const ex = beatToX(event.startBeat);
            const ew = eventWidth(event.lengthBeats);
            const fadeInPx = Math.min(ew, eventWidth(event.fadeInBeats || 0));
            const fadeOutPx = Math.min(ew, eventWidth(event.fadeOutBeats || 0));

            if (fadeInPx > 2) {
               const cp1x = event.fadeInCp1X !== undefined ? event.fadeInCp1X : 1/3;
               const cp1y = event.fadeInCp1Y !== undefined ? event.fadeInCp1Y : 1/3;
               const cp2x = event.fadeInCp2X !== undefined ? event.fadeInCp2X : 2/3;
               const cp2y = event.fadeInCp2Y !== undefined ? event.fadeInCp2Y : 2/3;
               const h1X = ex + cp1x * fadeInPx, h1Y = ey_t + eh_t - cp1y * eh_t;
               const h2X = ex + cp2x * fadeInPx, h2Y = ey_t + eh_t - cp2y * eh_t;
               if (Math.abs(x - h1X) < fadeHandleR && Math.abs(y - h1Y) < fadeHandleR) {
                  return { trackId: track.id, eventIdx: j, trackIdx: ti, mode: 'fade-in-curve1', beat: xToBeat(x), event };
               }
               if (Math.abs(x - h2X) < fadeHandleR && Math.abs(y - h2Y) < fadeHandleR) {
                  return { trackId: track.id, eventIdx: j, trackIdx: ti, mode: 'fade-in-curve2', beat: xToBeat(x), event };
               }
            }
            if (fadeOutPx > 2) {
               const cp1x = event.fadeOutCp1X !== undefined ? event.fadeOutCp1X : 1/3;
               const cp1y = event.fadeOutCp1Y !== undefined ? event.fadeOutCp1Y : 2/3;
               const cp2x = event.fadeOutCp2X !== undefined ? event.fadeOutCp2X : 2/3;
               const cp2y = event.fadeOutCp2Y !== undefined ? event.fadeOutCp2Y : 1/3;
               const h1X = ex + ew - fadeOutPx + cp1x * fadeOutPx, h1Y = ey_t + eh_t - cp1y * eh_t;
               const h2X = ex + ew - fadeOutPx + cp2x * fadeOutPx, h2Y = ey_t + eh_t - cp2y * eh_t;
               if (Math.abs(x - h1X) < fadeHandleR && Math.abs(y - h1Y) < fadeHandleR) {
                  return { trackId: track.id, eventIdx: j, trackIdx: ti, mode: 'fade-out-curve1', beat: xToBeat(x), event };
               }
               if (Math.abs(x - h2X) < fadeHandleR && Math.abs(y - h2Y) < fadeHandleR) {
                  return { trackId: track.id, eventIdx: j, trackIdx: ti, mode: 'fade-out-curve2', beat: xToBeat(x), event };
               }
            }
         }
      }

      const trackIdx = yToTrackIndex(y);
      if (trackIdx < 0) {
         if (y < state.rulerHeight) {
            return { mode: 'ruler', beat: xToBeat(x) };
         }
         return null;
      }

      // Check if this is an automation track lane — find the clip at this beat position
      const laneClips = state.autoTracks.filter(a => a.trackIdx === trackIdx);
      if (laneClips.length > 0) {
         const beat = xToBeat(x);
         // Find the clip that contains this beat (only if actually within bounds)
         const autoTrack = laneClips.find(a => {
            const sb = a.startBeat || 0;
            const lb = a.lengthBeats || 4;
            return beat >= sb && beat <= sb + lb;
         });
         if (autoTrack) {
            const startBeat = autoTrack.startBeat || 0;
            const lenBeats = autoTrack.lengthBeats || 4;
            const ax = beatToX(startBeat);
            const aw = eventWidth(lenBeats);
            // When zoomed out far, clips are too small for resize handles — move only
            if (aw >= 20) {
               const edgeThreshold = 6;
               const nearLeft = Math.abs(x - ax) < edgeThreshold;
               const nearRight = Math.abs(x - (ax + aw)) < edgeThreshold;
               if (nearLeft) return { mode: 'auto-resize-left', auto: autoTrack, trackIdx, beat };
               if (nearRight) return { mode: 'auto-resize-right', auto: autoTrack, trackIdx, beat };
            }
            return { mode: 'auto-move', auto: autoTrack, trackIdx, beat, dragStartBeat: beat - startBeat };
         }
         // Empty area on an auto lane — return distinct mode so context menu and click can differ
         return { mode: 'empty-auto', beat, trackIdx, autoLaneParam: laneClips[0].paramName, autoLaneParentId: laneClips[0].parentId };
      }

      if (trackIdx >= state.tracks.length) {
         return null;
      }

      const track = state.tracks[trackIdx];
      const beat = xToBeat(x);
      const ty = trackIndexToY(trackIdx);
      const ey = ty + 14;
      const eh = state.trackHeight - 18;

            // Check events (reverse order)
      for (let j = track.events.length - 1; j >= 0; j--) {
         const event = track.events[j];
         const ex = beatToX(event.startBeat);
         const ew = eventWidth(event.lengthBeats);
         if (beat >= event.startBeat && beat <= event.startBeat + event.lengthBeats) {
            // When zoomed out far, clips are too small for handles — move only
            if (ew >= 20) {
               const edgeThreshold = 6;
               const nearLeft = Math.abs(x - ex) < edgeThreshold;
               const nearRight = Math.abs(x - (ex + ew)) < edgeThreshold;

               const isMidiClipEvt = (event.type === 'stepseq' || event.type === 'slicer' || event.type === 'pianoroll');

               // Check volume handle (small centered handle on top edge) — skip for MIDI clips
               if (!isMidiClipEvt) {
                  const handleW = 16;
                  const handleH = 5;
                  const hx = ex + ew / 2 - handleW / 2;
                  const hy = ey - handleH - 1;
                  if (x >= hx && x <= hx + handleW && y >= hy && y <= hy + handleH) {
                     return { trackId: track.id, eventIdx: j, trackIdx, mode: 'volume', beat, event };
                  }
               }

               // Resize edges — work for both audio and MIDI clips
               if (y > ey + 4) {
                  if (nearLeft) return { trackId: track.id, eventIdx: j, trackIdx, mode: 'resize-left', beat, event };
                  if (nearRight) return { trackId: track.id, eventIdx: j, trackIdx, mode: 'resize-right', beat, event };
               }

               // Fade handles — skip for MIDI clips
               if (!isMidiClipEvt) {
                  const fadeInPx = Math.min(ew, eventWidth(event.fadeInBeats || 0));
                  const fadeOutPx = Math.min(ew, eventWidth(event.fadeOutBeats || 0));
                  const fadeHandleR = 7;

                  // (Curve handles are checked in the first pass above — works even outside beat range)

                  // Fade length handles at top edge (y near ey)
                  // When fade=0, handle is at the corner — only grabbable from the very top
                  if (Math.abs(x - (ex + fadeInPx)) < fadeHandleR && y < ey + 4) {
                     return { trackId: track.id, eventIdx: j, trackIdx, mode: 'fade-in', beat, event };
                  }
                  if (Math.abs(x - (ex + ew - fadeOutPx)) < fadeHandleR && y < ey + 4) {
                     return { trackId: track.id, eventIdx: j, trackIdx, mode: 'fade-out', beat, event };
                  }
               }
            }

            return {
               trackId: track.id, eventIdx: j, trackIdx,
               mode: 'move',
               beat, event,
            };
         }
      }
      return { trackId: track.id, trackIdx, mode: 'empty', beat };
   }

   // ---- Edge auto-scroll helpers ----
   function startEdgeScrollTimer() {
      if (state.edgeScrollTimer) return;
      state.edgeScrollTimer = setInterval(() => {
         if (state.edgeScrollDir !== 0 && state.dragMode) {
            const speed = 0.4 * state.edgeScrollStrength;
            let delta = 0;
            if (state.edgeScrollDir < 0) {
               const newScroll = Math.max(0, state.scrollLeft - speed);
               delta = newScroll - state.scrollLeft;
               state.scrollLeft = newScroll;
            } else {
               state.scrollLeft = state.scrollLeft + speed;
               delta = speed;
            }
            // Adjust drag data so clip stays under the mouse when it re-enters
            // Without this, the scroll changes xToBeat() at the same pixel,
            // causing the clip to jump when the mouse re-enters the canvas
            if (delta !== 0 && state.dragData) {
               if (state.dragData.startMouseBeat !== undefined) {
                  state.dragData.startMouseBeat += delta;
               }
               if (state.dragData.mouseOffsetBeat !== undefined) {
                  state.dragData.mouseOffsetBeat += delta;
               }
               if (state.dragData.dragStartBeat !== undefined) {
                  state.dragData.dragStartBeat += delta;
               }
               // For event drags: update _origStart on all selected events
               for (const s of state.selectedEvents) {
                  const track = state.tracks.find(t => t.id === s.trackId);
                  if (track && track.events[s.eventIdx] && track.events[s.eventIdx]._origStart !== undefined) {
                     track.events[s.eventIdx]._origStart += delta;
                  }
               }
               // For auto-resize: update origStart
               if (state.dragData.origStart !== undefined) {
                  state.dragData.origStart += delta;
               }
            }
            render();
         }
      }, 30);
   }
   function stopEdgeScrollTimer() {
      if (state.edgeScrollTimer) {
         clearInterval(state.edgeScrollTimer);
         state.edgeScrollTimer = null;
      }
      state.edgeScrollDir = 0;
      state.edgeScrollStrength = 0;
   }

   // ---- Mouse interaction ----
   function onMouseDown(e) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const hit = hitTest(x, y);

      // Hide context menu on any click
      hideContextMenu();

      if (e.button === 2) {
         // Right-click is handled by the 'contextmenu' event listener
         return;
      }

      // Select the audio track whenever clicking on anything that has a trackId
      // (events, resize handles, volume handles, fade handles, empty track area)
      if (hit && hit.trackId) {
         // Only switch panel for main event body or empty area, not for handle adjustments
         const isHandleMode = (hit.mode === 'resize-left' || hit.mode === 'resize-right' ||
            hit.mode === 'volume' || hit.mode === 'fade-in' || hit.mode === 'fade-out' ||
            hit.mode === 'fade-in-curve1' || hit.mode === 'fade-in-curve2' ||
            hit.mode === 'fade-out-curve1' || hit.mode === 'fade-out-curve2');
         state.onEventSelected({ trackId: hit.trackId, eventIdx: hit.eventIdx, event: hit.event, _selectOnly: isHandleMode });
      }

      // Loop handle drag
      if (hit && (hit.mode === 'loop-start' || hit.mode === 'loop-end')) {
         state.dragMode = hit.mode;
         state.dragData = {};
         return;
      }

      // Ruler click: move playhead (drag to scrub) or shift+drag to scroll
      if (hit && hit.mode === 'ruler') {
         if (e.shiftKey) {
            state.dragMode = 'ruler-drag';
            state.dragData = { startX: x, scrollLeft: state.scrollLeft };
            canvas.style.cursor = 'grabbing';
         } else {
            state.playheadBeat = snapBeat(hit.beat);
            state.onPlayheadMoved(state.playheadBeat);
            state.dragMode = 'playhead-drag';
            state.dragData = {};
         }
         render();
         return;
      }

      if (state.tool === 'pencil' && hit && hit.mode === 'empty') {
         // Pencil tool: create new event placeholder (actual creation via drop)
         // For now, pencil just moves playhead
         state.playheadBeat = snapBeat(hit.beat);
         state.onPlayheadMoved(state.playheadBeat);
         clearSelection();
         state.onEventSelected(null);
         render();
         return;
      }

      if (!hit) {
         // Clicked outside everything — move playhead
         state.playheadBeat = snapBeat(xToBeat(x));
         state.onPlayheadMoved(state.playheadBeat);
         clearSelection();
         state.onEventSelected(null);
         render();
         return;
      }

      // Click on automation clip
      if (hit.mode === 'automation' || hit.mode === 'auto-move') {
         // Select the clip — panel switch happens on mouseup (click vs drag)
         state.selectedAutoClip = hit.auto;
         // Start drag-move
         pushHistory();
         state.dragMode = 'auto-move';
         state.dragData = { auto: hit.auto, startBeat: hit.auto.startBeat, dragStartBeat: hit.dragStartBeat || 0, didDrag: false, startX: x, startY: y };
         startEdgeScrollTimer();
         render();
         return;
      }
      if (hit.mode === 'auto-resize-left') {
         state.selectedAutoClip = hit.auto;
         pushHistory();
         state.dragMode = 'auto-resize-left';
         state.dragData = { auto: hit.auto, origStart: hit.auto.startBeat, origLength: hit.auto.lengthBeats, didDrag: false };
         startEdgeScrollTimer();
         render();
         return;
      }
      if (hit.mode === 'auto-resize-right') {
         state.selectedAutoClip = hit.auto;
         pushHistory();
         state.dragMode = 'auto-resize-right';
         state.dragData = { auto: hit.auto, origStart: hit.auto.startBeat, origLength: hit.auto.lengthBeats, didDrag: false };
         startEdgeScrollTimer();
         render();
         return;
      }

      // Click on empty area of an auto lane — switch to auto panel
      if (hit.mode === 'empty-auto') {
         state.selectedAutoClip = null;
         state.onAutoLaneClick && state.onAutoLaneClick(hit.autoLaneParentId, hit.autoLaneParam);
         // Move playhead
         state.playheadBeat = snapBeat(hit.beat);
         state.onPlayheadMoved(state.playheadBeat);
         render();
         return;
      }

      if (hit.mode === 'empty') {
         // Clear auto clip selection when clicking empty area
         state.selectedAutoClip = null;
         // Click empty area: start selection box (ctrl or shift) or move playhead
         if (e.shiftKey || e.ctrlKey) {
            // Start selection box
            state.dragMode = 'select-box';
            state.dragData = { startX: x, startY: y, currentX: x, currentY: y };
            if (!e.shiftKey && !e.ctrlKey) clearSelection();
         } else {
            state.playheadBeat = snapBeat(hit ? hit.beat : xToBeat(x));
            state.onPlayheadMoved(state.playheadBeat);
            clearSelection();
            state.onEventSelected(null);
         }
         render();
         return;
      }

      // Click on event
      state.selectedAutoClip = null; // clear auto selection when clicking an event
      // Ctrl on resize edges = stretch mode, NOT additive selection
      // Ctrl on move = additive selection (toggle)
      const isResizeMode = (hit.mode === 'resize-left' || hit.mode === 'resize-right');
      const additive = (e.shiftKey || (e.ctrlKey && !isResizeMode));
      if (additive) {
         selectEvent(hit.trackId, hit.eventIdx, true, e.ctrlKey);
      } else {
         if (!isEventSelected(hit.trackId, hit.eventIdx)) {
            selectEvent(hit.trackId, hit.eventIdx);
         }
      }

      // Start drag if not additive
      if (!additive) {
         // Push history before any drag that modifies events
         if (hit.mode === 'move' || hit.mode === 'resize-left' || hit.mode === 'resize-right' ||
             hit.mode === 'volume' || hit.mode === 'fade-in' || hit.mode === 'fade-out' ||
             hit.mode === 'fade-in-curve' || hit.mode === 'fade-out-curve' ||
             hit.mode === 'fade-in-curve1' || hit.mode === 'fade-in-curve2' ||
             hit.mode === 'fade-out-curve1' || hit.mode === 'fade-out-curve2') {
            pushHistory();
         }
         state.dragMode = hit.mode;
         state.dragData = {
            startBeat: hit.event.startBeat,
            lengthBeats: hit.event.lengthBeats,
            startMouseBeat: hit.beat,
            // Offset from clip start to mouse position (in beats, unsnapped)
            // This ensures the clip always stays under the mouse
            mouseOffsetBeat: hit.beat - hit.event.startBeat,
            // Ctrl key at drag start: if held, resize time-stretches; if not, resize trims/loops
            stretchMode: !!(e.ctrlKey && (hit.mode === 'resize-left' || hit.mode === 'resize-right')),

            volume: hit.event.volume || 1.0,
            fadeInBeats: hit.event.fadeInBeats || 0,
            fadeOutBeats: hit.event.fadeOutBeats || 0,
            fadeInCurve: hit.event.fadeInCurve || 0,
            fadeOutCurve: hit.event.fadeOutCurve || 0,
            startMouseY: y,
            // Store original track index per selected event for free vertical drag
            origTrackIdxs: state.selectedEvents.map(s => {
               const t = state.tracks.findIndex(t => t.id === s.trackId);
               return t;
            }),
         };
      }

      // Start edge auto-scroll timer if a drag began
      if (state.dragMode) startEdgeScrollTimer();

      // Notify selection
      const sel = getSelectedEvents();
      state.onEventSelected(sel.length === 1 ? sel[0] : (sel.length > 1 ? sel : null));
      render();
   }

   function onMouseMove(e) {
      // ---- Automation clip drag handling ----
      if (state.dragMode && state.dragData && state.dragData.auto) {
         const rect = canvas.getBoundingClientRect();
         const mx = (e.clientX - rect.left);
         // Freeze clip when mouse leaves canvas — resume when it re-enters
         if (mx < 0 || mx > rect.width) return;
         const beat = xToBeat(mx);
         const auto = state.dragData.auto;
         if (state.dragData) state.dragData.didDrag = true;
         if (state.dragMode === 'auto-move') {
            let newStart = beat - state.dragData.dragStartBeat;
            newStart = snapBeat(Math.max(0, newStart));
            // Prevent overlap with other segments on same track+param
            newStart = resolveOverlap(auto.parentId, auto.paramName, newStart, auto.lengthBeats, auto.id);
            auto.startBeat = newStart;
         } else if (state.dragMode === 'auto-resize-left') {
            let newStart = Math.min(state.dragData.origStart + state.dragData.origLength - 0.25, Math.max(0, snapBeat(beat)));
            // Prevent growing into the previous segment on same track+param
            const prevSeg = state.autoTracks
               .filter(a => String(a.parentId) === String(auto.parentId) && a.paramName === auto.paramName && a.id !== auto.id && (a.startBeat + a.lengthBeats) <= state.dragData.origStart + state.dragData.origLength)
               .sort((a, b) => (b.startBeat + b.lengthBeats) - (a.startBeat + a.lengthBeats))[0];
            if (prevSeg) {
               newStart = Math.max(newStart, prevSeg.startBeat + prevSeg.lengthBeats);
            }
            const delta = newStart - state.dragData.origStart;
            auto.startBeat = newStart;
            auto.lengthBeats = Math.max(0.25, state.dragData.origLength - delta);
         } else if (state.dragMode === 'auto-resize-right') {
            let newEnd = Math.max(state.dragData.origStart + 0.25, snapBeat(beat));
            // Prevent growing into the next segment on same track+param
            const nextSeg = state.autoTracks
               .filter(a => String(a.parentId) === String(auto.parentId) && a.paramName === auto.paramName && a.id !== auto.id && a.startBeat >= state.dragData.origStart)
               .sort((a, b) => a.startBeat - b.startBeat)[0];
            if (nextSeg) {
               newEnd = Math.min(newEnd, nextSeg.startBeat);
            }
            auto.lengthBeats = Math.max(0.25, newEnd - state.dragData.origStart);
         }
         state.onAutomationDragged(auto);
         render();
         return;
      }
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (state.dragMode === 'resize-right' || state.dragMode === 'resize-left') {
}

      // ---- Edge auto-scroll during drag ----
      if (state.dragMode && state.dragMode !== 'select-box' && state.dragMode !== 'ruler-drag') {
         const edgeZone = 50; // pixels from edge to trigger auto-scroll
         if (x < edgeZone && x >= 0) {
            state.edgeScrollDir = -1;
            state.edgeScrollStrength = (edgeZone - x) / edgeZone;
         } else if (x > rect.width - edgeZone && x <= rect.width) {
            state.edgeScrollDir = 1;
            state.edgeScrollStrength = (x - (rect.width - edgeZone)) / edgeZone;
         } else {
            state.edgeScrollDir = 0;
            state.edgeScrollStrength = 0;
         }
      }

      // Freeze clip/event when mouse leaves canvas — resume when it re-enters
      // This prevents the clip from jumping to a random position outside the canvas
      if (state.dragMode && state.dragMode !== 'ruler-drag' && state.dragMode !== 'select-box') {
         if (x < 0 || x > rect.width) return;
      }

      if (!state.dragMode) {
         // Only update cursor when mouse is over the canvas
         if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
         // Update cursor
         const hit = hitTest(x, y);
         // Resize edges: ew-resize for trim, col-resize for stretch (Ctrl held)
         if (hit && (hit.mode === 'resize-left' || hit.mode === 'resize-right')) {
            canvas.style.cursor = e.ctrlKey ? 'col-resize' : 'ew-resize';
         }
         else if (hit && hit.mode === 'volume') canvas.style.cursor = 'ns-resize';
         else if (hit && hit.mode === 'fade-in') canvas.style.cursor = 'ew-resize';
         else if (hit && hit.mode === 'fade-out') canvas.style.cursor = 'ew-resize';
         else if (hit && (hit.mode === 'fade-in-curve1' || hit.mode === 'fade-in-curve2' ||
                          hit.mode === 'fade-out-curve1' || hit.mode === 'fade-out-curve2'))
            canvas.style.cursor = 'move';
         else if (hit && hit.mode === 'loop-start') canvas.style.cursor = 'ew-resize';
         else if (hit && hit.mode === 'loop-end') canvas.style.cursor = 'ew-resize';
         else if (hit && hit.mode === 'move') canvas.style.cursor = 'move';
         else if (hit && hit.mode === 'ruler') canvas.style.cursor = e.shiftKey ? 'grab' : 'pointer';
         else if (hit && hit.mode === 'empty' && (e.ctrlKey || e.shiftKey)) canvas.style.cursor = 'crosshair';
         else canvas.style.cursor = state.tool === 'pencil' ? 'crosshair' : 'default';
         return;
      }

      // Set cursor during resize drag to show trim vs stretch mode
      if (state.dragMode === 'resize-left' || state.dragMode === 'resize-right') {
         canvas.style.cursor = state.dragData.stretchMode ? 'col-resize' : 'ew-resize';
      }

      if (state.dragMode === 'ruler-drag') {
         const dx = x - state.dragData.startX;
         state.scrollLeft = Math.max(0, state.dragData.scrollLeft - dx / state.pxPerBeat);
         render();
         return;
      }

      if (state.dragMode === 'playhead-drag') {
         const rawBeat = xToBeat(x);
         state.playheadBeat = Math.max(0, snapBeat(rawBeat));
         state.onPlayheadMoved(state.playheadBeat);
         render();
         return;
      }

      if (state.dragMode === 'loop-start') {
         const rawBeat = xToBeat(x);
         state.loopStart = Math.max(0, Math.min(state.loopEnd - 0.25, snapBeat(rawBeat)));
         state.onLoopChanged(state.loopEnabled, state.loopStart, state.loopEnd);
         render();
         return;
      }

      if (state.dragMode === 'loop-end') {
         const rawBeat = xToBeat(x);
         state.loopEnd = Math.max(state.loopStart + 0.25, snapBeat(rawBeat));
         state.onLoopChanged(state.loopEnabled, state.loopStart, state.loopEnd);
         render();
         return;
      }

      if (state.dragMode === 'select-box') {
         state.dragData.currentX = x;
         state.dragData.currentY = y;
         // Select events intersecting the box
         const sx1 = Math.min(state.dragData.startX, x);
         const sy1 = Math.min(state.dragData.startY, y);
         const sx2 = Math.max(state.dragData.startX, x);
         const sy2 = Math.max(state.dragData.startY, y);
         state.selectedEvents = [];
         for (let i = 0; i < state.tracks.length; i++) {
            const ty = trackIndexToY(i);
            if (ty + state.trackHeight < sy1 || ty > sy2) continue;
            const track = state.tracks[i];
            for (let j = 0; j < track.events.length; j++) {
               const ev = track.events[j];
               const ex1 = beatToX(ev.startBeat);
               const ex2 = beatToX(ev.startBeat + ev.lengthBeats);
               if (ex2 < sx1 || ex1 > sx2) continue;
               state.selectedEvents.push({ trackId: track.id, eventIdx: j });
            }
         }
         render();
         return;
      }

      // Move / resize / volume for all selected events
      // Use RAW unsnapped delta so the event follows the mouse exactly,
      // then snap only the final position. This prevents drift.
      const rawBeat = xToBeat(x);
      const deltaBeat = rawBeat - state.dragData.startMouseBeat;

      if (state.dragMode === 'move') {
         // Compute target track from mouse Y
         const targetTrackIdx = yToTrackIndex(y);
         const dyTracks = targetTrackIdx >= 0 ? targetTrackIdx - state.dragData.origTrackIdxs[0] : 0;

         // Use mouseOffsetBeat so clip stays exactly under the mouse
         // The raw beat at mouse position minus the offset gives the clip start
         const newStartRaw = rawBeat - state.dragData.mouseOffsetBeat;

         for (let si = 0; si < state.selectedEvents.length; si++) {
            const s = state.selectedEvents[si];
            const origTrackIdx = state.dragData.origTrackIdxs[si];
            if (origTrackIdx < 0) continue;
            const origTrack = state.tracks[origTrackIdx];
            if (!origTrack) continue;
            const event = origTrack.events[s.eventIdx];
            if (!event) continue;

            if (event._origStart === undefined) {
               event._origStart = event.startBeat;
            }
            // Snap the final position, but keep the relationship to mouse
            event.startBeat = Math.max(0, snapBeat(event._origStart + (newStartRaw - state.dragData.startBeat)));

            // Move to different track if mouse is over another track
            const newTrackIdx = Math.max(0, Math.min(state.tracks.length - 1, origTrackIdx + dyTracks));
            if (newTrackIdx !== origTrackIdx) {
               const newTrack = state.tracks[newTrackIdx];
               if (newTrack && newTrack.id !== s.trackId) {
                  const oldIdx = origTrack.events.indexOf(event);
                  if (oldIdx >= 0) origTrack.events.splice(oldIdx, 1);
                  newTrack.events.push(event);
                  s.trackId = newTrack.id;
                  s.eventIdx = newTrack.events.length - 1;
                  // Re-anchor drag reference points to current position so the
                  // clip doesn't jump when changing tracks. All delta math is
                  // relative to these, so they must move with the clip.
                  event._origStart = event.startBeat;
                  state.dragData.startBeat = event.startBeat;
                  state.dragData.startMouseBeat = rawBeat;
                  state.dragData.mouseOffsetBeat = rawBeat - event.startBeat;
                  state.dragData.origTrackIdxs[si] = newTrackIdx;
               }
            }
         }
      } else if (state.dragMode === 'resize-right') {
         const isStretch = state.dragData.stretchMode;
         for (const s of state.selectedEvents) {
            const track = state.tracks.find(t => t.id === s.trackId);
            if (!track) continue;
            const event = track.events[s.eventIdx];
            if (!event) continue;
            const isMidiClip = (event.type === 'stepseq' || event.type === 'slicer' || event.type === 'pianoroll');
            if (event._origLength === undefined) event._origLength = event.lengthBeats;
            if (event._origStretched === undefined) event._origStretched = event.stretched || false;
            const newLen = snapBeat(event._origLength + deltaBeat);
            if (newLen > 0.1) {
               event.lengthBeats = newLen;
               if (isMidiClip) {
                  // MIDI clips: just change length, sequence loops to fill — no stretch/bake
               } else if (isStretch) {
                  // Ctrl+drag: stretch to fit new length, bake on mouseup
                  event.stretched = true;
                  event._needsBake = true;
               } else if (event._origStretched) {
                  // Was stretched but buffer not yet baked (edge case): bake now
                  event._needsBake = true;
                  event.stretched = false;
               }
               // else: normal trim/loop on already-baked or never-stretched buffer
            }
         }
      } else if (state.dragMode === 'resize-left') {
         const isStretch = state.dragData.stretchMode;
         for (const s of state.selectedEvents) {
            const track = state.tracks.find(t => t.id === s.trackId);
            if (!track) continue;
            const event = track.events[s.eventIdx];
            if (!event) continue;
            const isMidiClip = (event.type === 'stepseq' || event.type === 'slicer' || event.type === 'pianoroll');
            if (event._origStart === undefined) event._origStart = event.startBeat;
            if (event._origLength === undefined) event._origLength = event.lengthBeats;
            if (event._origStretched === undefined) event._origStretched = event.stretched || false;
            const rawNewStart = event._origStart + deltaBeat;
            const newStart = Math.max(0, snapBeat(rawNewStart));
            const newLen = event._origLength - (newStart - event._origStart);
            if (newLen > 0.1) {
               event.startBeat = newStart;
               event.lengthBeats = newLen;
               if (isMidiClip) {
                  // MIDI clips: just change length, sequence loops to fill — no stretch/bake
               } else if (isStretch) {
                  event.stretched = true;
                  event._needsBake = true;
               } else if (event._origStretched) {
                  event._needsBake = true;
                  event.stretched = false;
               }
            }
         }
      } else if (state.dragMode === 'volume') {
         const dy = state.dragData.startMouseY - y;
         const newVol = Math.max(0, Math.min(1.5, state.dragData.volume + dy / 50));
         for (const s of state.selectedEvents) {
            const track = state.tracks.find(t => t.id === s.trackId);
            if (!track) continue;
            const event = track.events[s.eventIdx];
            if (!event) continue;
            event.volume = newVol;
         }
      } else if (state.dragMode === 'fade-in') {
         // Drag fade-in handle horizontally to adjust fade length
         // Limited by the fade-out handle so they never overlap
         const rawBeat = xToBeat(x);
         for (const s of state.selectedEvents) {
            const track = state.tracks.find(t => t.id === s.trackId);
            if (!track) continue;
            const event = track.events[s.eventIdx];
            if (!event) continue;
            const fadeOutBeats = event.fadeOutBeats || 0;
            const maxFadeIn = state.dragData.lengthBeats - fadeOutBeats;
            const newFade = Math.max(0, Math.min(maxFadeIn, rawBeat - state.dragData.startBeat));
            event.fadeInBeats = state.snapEnabled ? snapBeat(newFade) : newFade;
         }
      } else if (state.dragMode === 'fade-out') {
         // Drag fade-out handle horizontally to adjust fade length
         // Limited by the fade-in handle so they never overlap
         const rawBeat = xToBeat(x);
         const eventEnd = state.dragData.startBeat + state.dragData.lengthBeats;
         for (const s of state.selectedEvents) {
            const track = state.tracks.find(t => t.id === s.trackId);
            if (!track) continue;
            const event = track.events[s.eventIdx];
            if (!event) continue;
            const fadeInBeats = event.fadeInBeats || 0;
            const maxFadeOut = state.dragData.lengthBeats - fadeInBeats;
            const newFade = Math.max(0, Math.min(maxFadeOut, eventEnd - rawBeat));
            event.fadeOutBeats = state.snapEnabled ? snapBeat(newFade) : newFade;
         }
      } else if (state.dragMode === 'fade-in-curve1' || state.dragMode === 'fade-in-curve2') {
         const which = state.dragMode === 'fade-in-curve1' ? 1 : 2;
         for (const s of state.selectedEvents) {
            const trackIdx = state.tracks.findIndex(t => t.id === s.trackId);
            if (trackIdx < 0) continue;
            const track = state.tracks[trackIdx];
            const event = track.events[s.eventIdx];
            if (!event) continue;
            const evStartX = beatToX(event.startBeat);
            const fiPx = Math.min(eventWidth(event.lengthBeats), eventWidth(event.fadeInBeats || 0));
            if (fiPx < 2) continue;
            const ey2 = trackIndexToY(trackIdx) + 14;
            const eh2 = state.trackHeight - 18;
            // X and Y are free — handles can go past the box for curve control
            // The curve itself is clamped to [0,1] in the drawing
            const normX = (x - evStartX) / fiPx;
            const normY = (ey2 + eh2 - y) / eh2;
            if (which === 1) { event.fadeInCp1X = normX; event.fadeInCp1Y = normY; }
            else { event.fadeInCp2X = normX; event.fadeInCp2Y = normY; }
         }
      } else if (state.dragMode === 'fade-out-curve1' || state.dragMode === 'fade-out-curve2') {
         const which = state.dragMode === 'fade-out-curve1' ? 1 : 2;
         for (const s of state.selectedEvents) {
            const trackIdx = state.tracks.findIndex(t => t.id === s.trackId);
            if (trackIdx < 0) continue;
            const track = state.tracks[trackIdx];
            const event = track.events[s.eventIdx];
            if (!event) continue;
            const evEndX = beatToX(event.startBeat + event.lengthBeats);
            const foPx = Math.min(eventWidth(event.lengthBeats), eventWidth(event.fadeOutBeats || 0));
            if (foPx < 2) continue;
            const ey2 = trackIndexToY(trackIdx) + 14;
            const eh2 = state.trackHeight - 18;
            const normX = (x - (evEndX - foPx)) / foPx;
            const normY = (ey2 + eh2 - y) / eh2;
            if (which === 1) { event.fadeOutCp1X = normX; event.fadeOutCp1Y = normY; }
            else { event.fadeOutCp2X = normX; event.fadeOutCp2Y = normY; }
         }
      }
      render();
   }

   function onMouseUp(e) {
      // Clean up temp properties
      for (const s of state.selectedEvents) {
         const track = state.tracks.find(t => t.id === s.trackId);
         if (!track) continue;
         const event = track.events[s.eventIdx];
         if (event) {
            delete event._origStart;
            delete event._origLength;
            delete event._origStretched;
         }
      }
      if (state.dragMode === 'select-box' || state.dragMode === 'move' ||
          state.dragMode === 'resize-left' || state.dragMode === 'resize-right' ||
          state.dragMode === 'volume' ||
          state.dragMode === 'fade-in' || state.dragMode === 'fade-out' ||
          state.dragMode === 'fade-in-curve' || state.dragMode === 'fade-out-curve') {
         state.onEventsChanged();
         // Re-render after events changed (buffer may have been baked)
         render();
      }
      if (state.dragMode === 'auto-move' || state.dragMode === 'auto-resize-left' || state.dragMode === 'auto-resize-right') {
         if (state.dragData && state.dragData.auto) {
            // Only fire onAutomationClick if this was a click (no drag movement)
            if (!state.dragData.didDrag) {
               state.onAutomationClick(state.dragData.auto);
            } else {
               state.onAutomationDragged(state.dragData.auto);
            }
         }
      }
      state.dragMode = null;
      state.dragData = null;
      stopEdgeScrollTimer();
      canvas.style.cursor = 'default';
   }

   // ---- Context menu ----
   // mode: 'event' | 'auto' | 'empty-audio' | 'empty-auto'
   // contextBeat: the beat position where the menu was opened (for paste)
   // autoClip: the automation clip that was right-clicked (for auto mode)
   // autoLaneParam/autoLaneParentId: for empty-auto mode, which param/track the lane is for
   function showContextMenu(clientX, clientY, contextBeat, mode, autoClip, trackIdx, autoLaneParam, autoLaneParentId) {
      if (!state.contextMenuEl) return;
      state.contextBeat = contextBeat !== undefined ? snapBeat(contextBeat) : state.playheadBeat;
      state.contextAutoClip = autoClip || null;
      state.contextTrackIdx = trackIdx !== undefined ? trackIdx : null;
      state.contextAutoParam = autoLaneParam || null;
      state.contextAutoParentId = autoLaneParentId || null;

      // Show/hide groups based on mode
      const groups = state.contextMenuEl.querySelectorAll('.lw-ctx-group');
      groups.forEach(g => {
         g.style.display = (g.dataset.ctxGroup === mode) ? '' : 'none';
      });

      // Show/hide paste items based on clipboard state
      const pasteEvents = state.contextMenuEl.querySelector('#lw-ctx-paste-events');
      if (pasteEvents) {
         pasteEvents.style.display = state.eventClipboard ? '' : 'none';
      }
      const pasteAuto = state.contextMenuEl.querySelector('#lw-ctx-paste-auto');
      if (pasteAuto) {
         pasteAuto.style.display = state.autoClipboard ? '' : 'none';
      }

      // Build track submenu (only for event mode)
      if (mode === 'event') {
         const submenu = state.contextMenuEl.querySelector('#lw-ctx-track-list');
         if (submenu) {
            submenu.innerHTML = '';
            for (const track of state.tracks) {
               const item = document.createElement('div');
               item.className = 'lw-ctx-item';
               item.textContent = track.name || `Track ${track.id}`;
               item.addEventListener('click', () => {
                  moveToTrack(track.id);
                  hideContextMenu();
               });
               submenu.appendChild(item);
            }
         }
      }

      // Build "New Automation" submenu (only for empty-audio mode)
      if (mode === 'empty-audio') {
         const autoSubmenu = state.contextMenuEl.querySelector('#lw-ctx-auto-new-list');
         if (autoSubmenu) {
            autoSubmenu.innerHTML = '';
            // Find the audio track that owns this lane
            let audioTrack = null;
            let laneIdx = 0;
            for (const t of state.tracks) {
               if (laneIdx === trackIdx) { audioTrack = t; break; }
               laneIdx++;
               const seenParams = new Set();
               for (const auto of state.autoTracks) {
                  if (String(auto.parentId) === String(t.id) && !seenParams.has(auto.paramName)) {
                     seenParams.add(auto.paramName);
                     laneIdx++;
                  }
               }
            }
            if (audioTrack) {
               const params = ['Volume', 'Pan', 'Pitch', 'Filter Cutoff', 'Filter Resonance'];
               for (const param of params) {
                  const item = document.createElement('div');
                  item.className = 'lw-ctx-item';
                  item.textContent = param;
                  item.addEventListener('click', () => {
                     createNewAutoSegment(audioTrack.id, param, state.contextBeat);
                     hideContextMenu();
                  });
                  autoSubmenu.appendChild(item);
               }
            }
         }
      }

      state.contextMenuEl.style.left = clientX + 'px';
      state.contextMenuEl.style.top = clientY + 'px';
      state.contextMenuEl.hidden = false;
   }

   function hideContextMenu() {
      if (state.contextMenuEl) state.contextMenuEl.hidden = true;
   }

   // Find a non-overlapping start beat for a new segment on the same track+param.
   // If atBeat overlaps, push it right until it doesn't.
   function resolveOverlap(parentId, paramName, startBeat, lengthBeats, excludeId) {
      let beat = startBeat;
      // Collect segments on the same track+param (excluding the one being moved)
      const segments = state.autoTracks
         .filter(a => String(a.parentId) === String(parentId) && a.paramName === paramName && a.id !== excludeId)
         .sort((a, b) => a.startBeat - b.startBeat);
      // Iteratively push past overlaps
      let changed = true;
      while (changed) {
         changed = false;
         for (const seg of segments) {
            const segEnd = seg.startBeat + seg.lengthBeats;
            const myEnd = beat + lengthBeats;
            if (beat < segEnd && myEnd > seg.startBeat) {
               // Overlap — push to end of this segment
               beat = segEnd;
               changed = true;
               break;
            }
         }
      }
      return beat;
   }

   function createNewAutoSegment(trackId, paramName, atBeat) {
      pushHistory();
      const uid = 'auto_' + trackId + '_' + paramName + '_' + Date.now().toString(36);
      const lengthBeats = 4;
      // Place after the last existing clip for this track+param, or at atBeat if none exist
      const existing = state.autoTracks
         .filter(a => String(a.parentId) === String(trackId) && a.paramName === paramName)
         .sort((a, b) => (b.startBeat + b.lengthBeats) - (a.startBeat + a.lengthBeats));
      const startBeat = existing.length > 0
         ? existing[0].startBeat + existing[0].lengthBeats
         : resolveOverlap(trackId, paramName, atBeat || 0, lengthBeats);
      // Inherit min/max from existing segments on the same lane (or from the callback)
      let aMin = 0, aMax = 1;
      if (existing.length > 0) {
         aMin = existing[0].min !== undefined ? existing[0].min : 0;
         aMax = existing[0].max !== undefined ? existing[0].max : 1;
      }
      if (state.onGetAutomationRange) {
         const r = state.onGetAutomationRange(trackId, paramName);
         if (r) { aMin = r.min; aMax = r.max; }
      }
      // Default Y = midpoint of the actual parameter range
      const defaultY = (aMin + aMax) / 2;
      const clip = {
         id: uid,
         parentId: trackId,
         paramName: paramName,
         points: [
            { x: 0, y: defaultY },
            { x: 1, y: defaultY },
         ],
         startBeat: startBeat,
         lengthBeats: lengthBeats,
         min: aMin,
         max: aMax,
      };
      state.autoTracks.push(clip);
      state.selectedAutoClip = clip;
      recalcAutoTrackIndices();
      render();
      // Load the new segment in the auto editor
      if (state.onAutomationClick) state.onAutomationClick(clip);
   }

   function recalcAutoTrackIndices() {
      let laneIdx = 0;
      for (const audioTrack of state.tracks) {
         laneIdx++; // audio track takes one lane
         // Group auto clips by paramName — one lane per unique param on this track
         const seenParams = new Set();
         for (const auto of state.autoTracks) {
            if (String(auto.parentId) === String(audioTrack.id)) {
               if (!seenParams.has(auto.paramName)) {
                  seenParams.add(auto.paramName);
                  laneIdx++; // new lane for this param
               }
               auto.trackIdx = laneIdx - 1; // all clips for same param share the same lane
            }
         }
      }
      // Orphans — group by paramName too
      const orphanParams = new Set();
      for (const auto of state.autoTracks) {
         const hasParent = state.tracks.some(t => String(t.id) === String(auto.parentId));
         if (!hasParent) {
            if (!orphanParams.has(auto.paramName)) {
               orphanParams.add(auto.paramName);
               laneIdx++;
            }
            auto.trackIdx = laneIdx - 1;
         }
      }
   }

   // ---- External API ----
   function setTracks(tracks) { state.tracks = tracks; render(); }
   function setBpm(bpm) { state.bpm = bpm; render(); }
   function setPlayhead(beat) { state.playheadBeat = beat; render(); }
   function setAutomations(autos) { state.automations = autos; render(); }
   function setAutoTracks(tracks) { state.autoTracks = tracks; render(); }

   function copyAutoClip(autoClip) {
      const src = autoClip || state.selectedAutoClip || (state.dragData && state.dragData.auto);
      if (!src) return;
      state.autoClipboard = {
         points: src.points.map(p => ({ ...p })),
         lengthBeats: src.lengthBeats,
         min: src.min,
         max: src.max,
         paramName: src.paramName,
         parentId: src.parentId,
      };
      state.eventClipboard = null; // clear event clipboard to avoid confusion
   }

   function pasteAutoClip(atBeat, targetTrackId) {
      if (!state.autoClipboard) return;
      pushHistory();
      // Determine target track: explicit target > clipboard's source track
      let parentId = targetTrackId || state.autoClipboard.parentId;
      const lengthBeats = state.autoClipboard.lengthBeats;
      const startBeat = resolveOverlap(parentId, state.autoClipboard.paramName, atBeat || 0, lengthBeats);
      // Generate a unique ID for this pasted clip instance
      const uid = 'auto_' + parentId + '_' + state.autoClipboard.paramName + '_' + Date.now().toString(36);
      const clip = {
         id: uid,
         parentId: parentId,
         paramName: state.autoClipboard.paramName,
         points: state.autoClipboard.points.map(p => ({ ...p })),
         startBeat: startBeat,
         lengthBeats: lengthBeats,
         min: state.autoClipboard.min,
         max: state.autoClipboard.max,
      };
      state.autoTracks.push(clip);
      state.selectedAutoClip = clip;
      recalcAutoTrackIndices();
      render();
      // Load the pasted clip in the auto editor
      if (state.onAutomationClick) state.onAutomationClick(clip);
   }

   function duplicateAutoClip(autoClip) {
      if (!autoClip) return;
      pushHistory();
      // Place duplicate right after the original, resolving any overlaps
      const desiredStart = autoClip.startBeat + autoClip.lengthBeats;
      const startBeat = resolveOverlap(autoClip.parentId, autoClip.paramName, desiredStart, autoClip.lengthBeats, autoClip.id);
      const uid = 'auto_' + autoClip.parentId + '_' + autoClip.paramName + '_' + Date.now().toString(36);
      const clip = {
         id: uid,
         parentId: autoClip.parentId,
         paramName: autoClip.paramName,
         points: autoClip.points.map(p => ({ ...p })),
         startBeat: startBeat,
         lengthBeats: autoClip.lengthBeats,
         min: autoClip.min,
         max: autoClip.max,
      };
      state.autoTracks.push(clip);
      state.selectedAutoClip = clip;
      recalcAutoTrackIndices();
      render();
      // Load the duplicated clip in the auto editor
      if (state.onAutomationClick) state.onAutomationClick(clip);
   }

   function deleteAutoClip(autoClip) {
      pushHistory();
      const idx = state.autoTracks.findIndex(a => a.id === autoClip.id);
      if (idx >= 0) {
         state.autoTracks.splice(idx, 1);
         if (state.selectedAutoClip === autoClip) state.selectedAutoClip = null;
         recalcAutoTrackIndices();
         render();
      }
   }
   function setZoom(pxPerBeat) { state.pxPerBeat = Math.max(1, Math.min(400, pxPerBeat)); render(); }
   function setScrollTop(st) { state.scrollTop = Math.max(0, st); render(); }
   function setLoop(enabled, start, end) {
      state.loopEnabled = enabled;
      if (start !== undefined) state.loopStart = start;
      if (end !== undefined) state.loopEnd = end;
      render();
   }
   function getLoop() {
      return { enabled: state.loopEnabled, start: state.loopStart, end: state.loopEnd };
   }

   // ---- Scrollbar ----
   function getTotalBeats() {
      // Compute from events, with a minimum and some padding
      let maxBeat = state.totalBeats;
      for (const track of state.tracks) {
         for (const ev of track.events) {
            const end = ev.startBeat + ev.lengthBeats;
            if (end > maxBeat) maxBeat = end;
         }
      }
      // Add padding (8 beats) and ensure minimum
      return Math.max(32, maxBeat + 8);
   }

   function updateScrollbarHandle() {
      if (!state.scrollbarEl || !state.scrollbarHandleEl) return;
      const total = getTotalBeats();
      const { w } = getCanvasSize();
      const visibleBeats = w / state.pxPerBeat;
      const maxScroll = Math.max(0, total - visibleBeats);

      // Clamp scrollLeft
      if (state.scrollLeft > maxScroll) state.scrollLeft = maxScroll;
      if (state.scrollLeft < 0) state.scrollLeft = 0;

      const barWidth = state.scrollbarEl.clientWidth;
      if (visibleBeats >= total) {
         // Everything fits — handle fills the bar
         state.scrollbarHandleEl.style.left = '0px';
         state.scrollbarHandleEl.style.width = barWidth + 'px';
      } else {
         const handleWidth = (visibleBeats / total) * barWidth;
         const handleLeft = (state.scrollLeft / total) * barWidth;
         state.scrollbarHandleEl.style.left = handleLeft + 'px';
         state.scrollbarHandleEl.style.width = Math.max(20, handleWidth) + 'px';
      }
   }

   function setScrollbarEls(scrollbarEl, handleEl) {
      state.scrollbarEl = scrollbarEl;
      state.scrollbarHandleEl = handleEl;

      // Drag the handle to scroll
      handleEl.addEventListener('mousedown', (e) => {
         e.preventDefault();
         e.stopPropagation();
         state.scrollbarDragging = true;
         state.scrollbarDragStartX = e.clientX;
         state.scrollbarDragStartScroll = state.scrollLeft;
         document.body.style.cursor = 'grabbing';
      });

      // Click on bar (not handle) to jump
      scrollbarEl.addEventListener('mousedown', (e) => {
         if (e.target === handleEl) return;
         const rect = scrollbarEl.getBoundingClientRect();
         const clickRatio = (e.clientX - rect.left) / rect.width;
         const total = getTotalBeats();
         const { w } = getCanvasSize();
         const visibleBeats = w / state.pxPerBeat;
         state.scrollLeft = Math.max(0, clickRatio * total - visibleBeats / 2);
         updateScrollbarHandle();
         render();
      });

      // Global mousemove for drag
      window.addEventListener('mousemove', (e) => {
         if (!state.scrollbarDragging) return;
         const rect = scrollbarEl.getBoundingClientRect();
         const dx = e.clientX - state.scrollbarDragStartX;
         const total = getTotalBeats();
         const ratio = dx / rect.width;
         state.scrollLeft = Math.max(0, state.scrollbarDragStartScroll + ratio * total);
         updateScrollbarHandle();
         render();
      });

      window.addEventListener('mouseup', () => {
         if (state.scrollbarDragging) {
            state.scrollbarDragging = false;
            document.body.style.cursor = '';
         }
      });
   }
   function setTool(tool) {
      state.tool = tool;
      canvas.style.cursor = tool === 'pencil' ? 'crosshair' : 'default';
   }

   function addEventToTrack(trackId, event) {
      pushHistory();
      const track = state.tracks.find(t => t.id === trackId);
      if (track) { track.events.push(event); render(); }
   }

   function getDropPosition(x, y) {
      const beat = snapBeat(xToBeat(x));
      const trackIdx = yToTrackIndex(y);
      const track = state.tracks[trackIdx];
      return { beat: Math.max(0, beat), trackIdx, trackId: track ? track.id : null };
   }

   function setContextMenuEl(el) { state.contextMenuEl = el; }
   function setFxChainCallbacks(snapshotFn, restoreFn) {
      state.snapshotFxChains = snapshotFn;
      state.restoreFxChains = restoreFn;
   }

   // ---- Mouse wheel: Ctrl+wheel=zoom, Shift+wheel=horizontal, plain=vertical ----
   canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      if (e.ctrlKey) {
         // Zoom (cursor-centered)
         const rect = canvas.getBoundingClientRect();
         const mouseX = e.clientX - rect.left;
         const beatAtMouse = xToBeat(mouseX);
         const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
         state.pxPerBeat = Math.max(1, Math.min(400, state.pxPerBeat * factor));
         state.scrollLeft = Math.max(0, beatAtMouse - mouseX / state.pxPerBeat);
      } else if (e.shiftKey) {
         // Horizontal scroll
         const scrollAmount = e.deltaY / state.pxPerBeat;
         state.scrollLeft = Math.max(0, state.scrollLeft + scrollAmount);
      } else {
         // Vertical scroll — sync with track-list
         const scrollAmount = e.deltaY > 0 ? state.trackHeight * 0.3 : -state.trackHeight * 0.3;
         const trackListEl = document.getElementById('lw-track-list');
         if (trackListEl) {
            trackListEl.scrollTop += scrollAmount;
            // The scroll event listener will call setScrollTop
         } else {
            state.scrollTop = Math.max(0, state.scrollTop + scrollAmount);
         }
      }
      render();
   }, { passive: false });

   // ---- Event listeners ----
   // Prevent native drag from interfering with Ctrl+drag operations
   canvas.addEventListener('dragstart', (e) => { e.preventDefault(); return false; });

   // mousedown on canvas; mousemove/mouseup on window so drags work outside canvas
   canvas.addEventListener('mousedown', onMouseDown);
   window.addEventListener('mousemove', onMouseMove);
   window.addEventListener('mouseup', onMouseUp);
   // Only prevent browser context menu when we show our custom menu (on events)
   canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const hit = hitTest(x, y);

      // Right-click on ruler: set loop region
      if (hit && hit.mode === 'ruler') {
         const clickedBeat = snapBeat(hit.beat);
         if (clickedBeat > state.playheadBeat) {
            state.loopStart = state.playheadBeat;
            state.loopEnd = clickedBeat;
         } else {
            state.loopStart = clickedBeat;
            state.loopEnd = state.playheadBeat;
         }
         state.loopEnabled = true;
         state.onLoopChanged(state.loopEnabled, state.loopStart, state.loopEnd);
         render();
         return;
      }

      // Right-click on an event: select it and show event context menu
      if (hit && hit.mode !== 'empty' && hit.mode !== 'empty-auto' && hit.mode !== 'ruler' &&
          hit.mode !== 'loop-start' && hit.mode !== 'loop-end' &&
          hit.mode !== 'auto-move' && hit.mode !== 'auto-resize-left' && hit.mode !== 'auto-resize-right') {
         if (!isEventSelected(hit.trackId, hit.eventIdx)) {
            selectEvent(hit.trackId, hit.eventIdx);
         }
         showContextMenu(e.clientX, e.clientY, hit.beat, 'event');
      } else if (hit && (hit.mode === 'auto-move' || hit.mode === 'auto-resize-left' || hit.mode === 'auto-resize-right')) {
         // Right-click on automation clip: show auto context menu
         showContextMenu(e.clientX, e.clientY, hit.beat, 'auto', hit.auto);
      } else if (hit && hit.mode === 'empty-auto') {
         // Right-click on empty area of an auto lane: show auto-empty context menu
         showContextMenu(e.clientX, e.clientY, hit.beat, 'empty-auto', null, hit.trackIdx, hit.autoLaneParam, hit.autoLaneParentId);
      } else {
         // Right-click on empty area of an audio track: show audio-empty context menu
         const clickedBeat = hit ? snapBeat(hit.beat) : snapBeat(xToBeat(x));
         const clickedTrackIdx = hit ? hit.trackIdx : yToTrackIndex(y);
         showContextMenu(e.clientX, e.clientY, clickedBeat, 'empty-audio', null, clickedTrackIdx);
      }
   });
   window.addEventListener('resize', resize);
   document.addEventListener('click', (e) => {
      if (state.contextMenuEl && !state.contextMenuEl.hidden && !state.contextMenuEl.contains(e.target)) {
         hideContextMenu();
      }
   });

   // ---- Initial sizing + layout change detection ----
   // Use ResizeObserver to detect when the canvas wrapper changes size
   // (split-grid drag, window resize, bucket panel toggle, etc.)
   const wrapEl = canvas.parentElement;
   if (wrapEl && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => { resize(); });
      ro.observe(wrapEl);
   } else {
      // Fallback: poll a few times until layout settles, then rely on window resize
      let attempts = 0;
      const tryResize = () => {
         const rect = canvas.getBoundingClientRect();
         if (rect.width > 0 && rect.height > 0) {
            resize();
         } else if (attempts++ < 10) {
            setTimeout(tryResize, 50);
         }
      };
      setTimeout(tryResize, 0);
   }

   return {
      state, render, resize,
      setTracks, setBpm, setPlayhead, setZoom, setScrollTop, setTool, setAutomations, setAutoTracks,
      copyAutoClip, pasteAutoClip, duplicateAutoClip, deleteAutoClip, createNewAutoSegment,
      setContextMenuEl, setScrollbarEls, setFxChainCallbacks,
      setLoop, getLoop,
      getDropPosition, addEventToTrack,
      clearSelection, selectAll,
      copySelected, cutSelected, pasteAt, duplicateSelected, splitSelected, reverseSelected, deleteSelected,
      moveToTrack, undo, redo,
      pushHistoryForExternal,
      beatToX, xToBeat, snapBeat,
   };
}

module.exports = { createTimeline };
